import { ebitdaAddbackGroup } from './ebitdaAddbacks'
import { GripVertical, PanelRightClose, PanelRightOpen, Redo2, Settings, Sparkles, Undo2, X } from 'lucide-react'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent, type MouseEvent as ReactMouseEvent, type ReactNode, type WheelEvent as ReactWheelEvent } from 'react'
import type { QuickBooksFpaLayoutNode, QuickBooksFpaStatementRender, QuickBooksFpaStatementRow } from '../../../data/ServerApiClient'
import { buildQuickBooksFpaInsertRowPatch, buildQuickBooksFpaMoveRowPatch, compareQuickBooksFpaLayoutNodes, normalizeQuickBooksFpaSortOrder } from '../quickBooksFpaLayoutRows'
import { cfoCurrencySymbol, formatAsOf } from './cfoFormat'
import { buildCfoStatementColumnSpecs, type CfoStatementComparisonMode, type CfoStatementColumnWindow } from './cfoStatementColumnSpecs'
import { resolveStatementSectionTotal, selectProfitAndLossSection, statementSectionTotalValue, statementRowParents, statementRowIsCollapsed, isCanonicalProfitAndLossControl } from './cfoStatementSections'

export type { CfoStatementComparisonMode } from './cfoStatementColumnSpecs'

export type CfoSegmentOption = {
  id: string
  label: ReactNode
  disabled?: boolean
}

export type CfoSegmentedControlProps = {
  label: string
  options: CfoSegmentOption[]
  value: string
  onChange: (value: string) => void
}

export function CfoSegmentedControl({ label, options, value, onChange }: CfoSegmentedControlProps) {
  const move = (event: KeyboardEvent<HTMLButtonElement>, direction: 1 | -1) => {
    const enabled = options.filter((option) => !option.disabled)
    if (!enabled.length) return
    const current = Math.max(0, enabled.findIndex((option) => option.id === value))
    const next = enabled[(current + direction + enabled.length) % enabled.length]
    if (!next) return
    event.preventDefault()
    onChange(next.id)
    requestAnimationFrame(() => {
      document
        .querySelector<HTMLButtonElement>(`[data-cfo-segment="${label}"][data-cfo-segment-value="${next.id}"]`)
        ?.focus()
    })
  }

  return (
    <div className="seg" role="tablist" aria-label={label}>
      {options.map((option) => {
        const active = option.id === value
        return (
          <button
            type="button"
            role="tab"
            key={option.id}
            className={active ? 'active' : undefined}
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            disabled={option.disabled}
            data-cfo-segment={label}
            data-cfo-segment-value={option.id}
            onClick={() => onChange(option.id)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowRight' || event.key === 'ArrowDown') move(event, 1)
              if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') move(event, -1)
              if (event.key === 'Home') {
                const first = options.find((option) => !option.disabled)
                if (first) {
                  event.preventDefault()
                  onChange(first.id)
                }
              }
              if (event.key === 'End') {
                const last = [...options].reverse().find((option) => !option.disabled)
                if (last) {
                  event.preventDefault()
                  onChange(last.id)
                }
              }
            }}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}

export type CfoControlRowProps = {
  children: ReactNode
}

export function CfoControlRow({ children }: CfoControlRowProps) {
  return <div className="controls rise">{children}</div>
}

export type CfoLoadingNoteProps = {
  label: ReactNode
  compact?: boolean
}

export function CfoLoadingNote({ label, compact = false }: CfoLoadingNoteProps) {
  return (
    <div className={`cfo-shell-loading-note${compact ? ' compact' : ''}`} role="status" aria-live="polite" aria-busy="true">
      <span className="cfo-shell-loading-orb" aria-hidden="true">
        <Sparkles size={16} />
      </span>
      <span>{label}</span>
    </div>
  )
}

export type CfoChartSkeletonProps = {
  label?: string
  className?: string
  style?: CSSProperties
}

export function CfoChartSkeleton({ label = 'Loading chart…', className = '', style }: CfoChartSkeletonProps) {
  return (
    <div
      className={`cfo-shell-skel cfo-shell-skel-chart ${className}`.trim()}
      style={style}
      role="status"
      aria-label={label}
      aria-busy="true"
    />
  )
}

export type CfoCardSkeletonsProps = {
  count?: number
  label?: string
}

export function CfoCardSkeletons({ count = 3, label = 'Loading cards…' }: CfoCardSkeletonsProps) {
  return (
    <div className="cards cfo-shell-card-skeletons" role="status" aria-label={label} aria-busy="true">
      {Array.from({ length: count }, (_, index) => (
        <article className="card cfo-shell-card-skeleton" key={`cfo-card-skeleton-${index}`} aria-hidden="true">
          <div className="body">
            <span className="cfo-shell-skel cfo-shell-skel-chip" />
            <div className="grow">
              <span className="cfo-shell-skel cfo-shell-skel-line wide" />
              <span className="cfo-shell-skel cfo-shell-skel-line" />
            </div>
            <div className="figc">
              <span className="cfo-shell-skel cfo-shell-skel-number" />
              <span className="cfo-shell-skel cfo-shell-skel-line tiny" />
            </div>
          </div>
        </article>
      ))}
    </div>
  )
}

export type CfoAiReadProps = {
  children: ReactNode
}

export function CfoAiRead({ children }: CfoAiReadProps) {
  return (
    <div className="airead">
      <div>{children}</div>
    </div>
  )
}

export type CfoNativeTableColumn<T> = {
  key: string
  header: ReactNode
  className?: string
  render: (row: T) => ReactNode
}

export type CfoNativeTableProps<T> = {
  columns: CfoNativeTableColumn<T>[]
  rows: T[]
  rowKey: (row: T, index: number) => string
  emptyLabel?: string
  maxHeight?: number
  loading?: boolean
  loadingLabel?: string
  loadingRows?: number
}

export type CfoTableSkeletonProps = {
  columns?: number
  rows?: number
  label?: string
  maxHeight?: number
}

export function CfoTableSkeleton({
  columns = 4,
  rows = 6,
  label = 'Loading table…',
  maxHeight,
}: CfoTableSkeletonProps) {
  const safeColumns = Math.max(1, columns)
  const safeRows = Math.max(1, rows)
  return (
    <div
      className="dtable-wrap cfo-shell-table-skeleton-wrap"
      style={maxHeight != null ? { maxHeight } : undefined}
      role="status"
      aria-label={label}
      aria-busy="true"
    >
      <table className="dtable cfo-shell-table-skeleton" aria-hidden="true">
        <thead>
          <tr>
            {Array.from({ length: safeColumns }, (_, index) => (
              <th key={`skel-head-${index}`}>
                <span className="cfo-shell-skel cfo-shell-skel-th" />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: safeRows }, (_, rowIndex) => (
            <tr key={`skel-row-${rowIndex}`}>
              {Array.from({ length: safeColumns }, (_, colIndex) => (
                <td key={`skel-cell-${rowIndex}-${colIndex}`}>
                  <span className={`cfo-shell-skel cfo-shell-skel-td${colIndex === 0 ? ' first' : ''}`} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function CfoNativeTable<T>({
  columns,
  rows,
  rowKey,
  emptyLabel = 'No rows available yet.',
  maxHeight,
  loading = false,
  loadingLabel = 'Loading table…',
  loadingRows = 6,
}: CfoNativeTableProps<T>) {
  if (loading) {
    return <CfoTableSkeleton columns={columns.length} rows={loadingRows} label={loadingLabel} maxHeight={maxHeight} />
  }

  return (
    <div className="dtable-wrap" style={maxHeight != null ? { maxHeight } : undefined}>
      <table className="dtable">
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key} className={column.className}>
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length ? (
            rows.map((row, index) => (
              <tr key={rowKey(row, index)}>
                {columns.map((column) => (
                  <td key={column.key} className={column.className}>
                    {column.render(row)}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={columns.length}>{emptyLabel}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

export type CfoStatementCellDecoration = {
  tone: 'edited-up' | 'edited-down'
  title: string
}

export type CfoStatementTableProps = {
  statement: QuickBooksFpaStatementRender | null
  percentOfSales?: boolean
  showCurrencyAndPercent?: boolean
  periodHeaderFormatter?: (period: string | null | undefined, index: number) => ReactNode
  endPeriod?: string | null
  comparisonMode?: CfoStatementComparisonMode
  maxPeriods?: number
  periodWindow?: CfoStatementColumnWindow
  maxRows?: number
  defaultCollapsed?: boolean
  defaultRollupDepth?: number
  rollupDepth?: number
  onRollupDepthChange?: (depth: number) => void
  fullscreen?: boolean
  toolbarLeading?: ReactNode
  toolbarActions?: ReactNode
  layoutName?: string | null
  onOpenGlDetail?: (detail: CfoStatementGlDetailRequest) => void
  onExplainCell?: (cell: CfoStatementGlDetailCell) => void
  onAnalyzeRow?: (row: QuickBooksFpaStatementRow) => void
  layoutEdit?: CfoStatementLayoutEditAdapter | null
  /** Read-only layout nodes (hidden lines, current names) for a page that
   * shows the advisor's layout without editing it — the forecast page, for
   * one. Ignored once `layoutEdit` supplies its own `nodes`. */
  layoutNodes?: QuickBooksFpaLayoutNode[] | null
  loading?: boolean
  loadingLabel?: string
  /** Shown when there is no statement AND nothing is loading. Say why, not "loading". */
  emptyLabel?: string
  hideBlankRows?: boolean
  cellDecorator?: (row: QuickBooksFpaStatementRow, periodIndex: number) => CfoStatementCellDecoration | null
  rowBadge?: (row: QuickBooksFpaStatementRow) => ReactNode
  /** Deep-link a statement row into view: set focusNodeId + bump focusNonce to scroll to it,
   * expanding its collapsed ancestors, and briefly highlight it. */
  focusNodeId?: string | null
  focusNonce?: number
}

export type CfoStatementLayoutPatch = {
  nodes?: Array<Partial<QuickBooksFpaLayoutNode> & { id: string; clear_parent?: boolean; clear_cash_flow_section?: boolean; clear_qbo_account_sub_type?: boolean }>
  delete_node_ids?: string[]
}

export type CfoStatementLayoutEditAdapter = {
  layoutId: string
  layoutName?: string | null
  layoutStatus?: string | null
  nodes: QuickBooksFpaLayoutNode[]
  editable: boolean
  busy?: boolean
  status?: string
  onPatch: (patch: CfoStatementLayoutPatch) => Promise<void> | void
  onPublish?: () => Promise<void> | void
  onValidate?: () => Promise<void> | void
  onOpenJournalEntry?: (nodeId: string) => void
}

export type CfoStatementGlDetailCell = {
  nodeId: string
  rowLabel: string
  statementType?: string | null
  accountId?: string | null
  accountNumber?: string | null
  accountName?: string | null
  columnKey: string
  columnLabel: string
  period?: string | null
  value: number | null
}

export type CfoStatementGlDetailRequest = CfoStatementGlDetailCell & {
  selectedCells?: CfoStatementGlDetailCell[]
}

type CfoStatementColumn = {
  key: string
  header: ReactNode
  headerLabel: string
  kind: 'value' | 'delta' | 'pct'
  period?: string | null
  periodIndex?: number
  read: (row: QuickBooksFpaStatementRow) => number | null
  readPctOfSales?: (row: QuickBooksFpaStatementRow) => number | null
}

type CfoStatementFeatureSettings = {
  hideBlankRows: boolean
  showDetailRows: boolean
  showAccountCodes: boolean
  cleanCollapsedProfitAndLoss: boolean
  enableCellSelection: boolean
  enableRightClickGlDetail: boolean
  showFullscreenEditActions: boolean
}

type CfoStatementCellCoord = {
  rowIndex: number
  columnIndex: number
}

type CfoStatementCellSelection = {
  anchor: CfoStatementCellCoord
  focus: CfoStatementCellCoord
}

type CfoStatementContextMenuState = {
  x: number
  y: number
  row: QuickBooksFpaStatementRow
  column?: CfoStatementColumn | null
  value?: number | null
} | null

type CfoStatementRowEditorState = {
  nodeId: string
  displayName: string
  statementType: string
  includeInReports: boolean
} | null

type CfoStatementUndoEntry = {
  id: string
  label: string
  before: CfoStatementLayoutPatch
  after: CfoStatementLayoutPatch
}

type CfoStatementLayoutChangePatch = Partial<QuickBooksFpaLayoutNode> & {
  id?: string
  clear_parent?: boolean
  clear_cash_flow_section?: boolean
}

type CfoStatementAppliedLayoutChangeEntry = {
  id: string
  nodeId: string
  before: CfoStatementLayoutChangePatch
  after: CfoStatementLayoutChangePatch
  createdAt: string
  actionLabel?: string
  actionSummary?: string
  sourceUndoEntryId?: string
  batch?: Array<{
    id?: string
    nodeId: string
    before: CfoStatementLayoutChangePatch
    after: CfoStatementLayoutChangePatch
  }>
}

type CfoStatementMergeGroup = {
  id: string
  label: string
  rowIds: string[]
  mode: 'merge' | 'rollup'
}

const STATEMENT_ROLLUP_MAX_DEPTH = 3
const STATEMENT_ADVANCED_PANEL_DEFAULT_WIDTH = 360
const STATEMENT_ADVANCED_PANEL_MIN_WIDTH = 300
const STATEMENT_ADVANCED_PANEL_MAX_WIDTH = 560

const clampStatementRollupDepth = (depth: number) =>
  Math.max(0, Math.min(STATEMENT_ROLLUP_MAX_DEPTH, Number.isFinite(depth) ? Math.round(depth) : 0))

const DEFAULT_STATEMENT_SETTINGS: CfoStatementFeatureSettings = {
  hideBlankRows: true,
  showDetailRows: true,
  showAccountCodes: true,
  cleanCollapsedProfitAndLoss: true,
  enableCellSelection: true,
  enableRightClickGlDetail: true,
  showFullscreenEditActions: true,
}

const isExplicitStatementTotalRow = (row: QuickBooksFpaStatementRow) => {
  const label = String(row.label || '').trim().replace(/\s+/g, ' ').toLowerCase()
  const physicalAccount = Boolean(row.account_id) || row.row_type === 'account' || row.row_type === 'line'
  return row.row_type === 'total' || (!physicalAccount && label.startsWith('total '))
}

const isProfitAndLossEbitdaSupportRow = (row: QuickBooksFpaStatementRow) =>
  row.parent_node_id === 'fg-cfo-pnl-rollup-ebitda' && String(row.node_id || '').startsWith('fg-cfo-pnl-rollup-ebitda:')

const rowClass = (row: QuickBooksFpaStatementRow) => {
  const classes: string[] = []
  if (
    row.node_id === 'fg-cfo-pnl-rollup-gross-profit' ||
    row.node_id === 'fg-cfo-pnl-rollup-operating-income' ||
    row.node_id === 'fg-cfo-pnl-rollup-net-income'
  ) classes.push('rollup-boundary')
  if (isProfitAndLossEbitdaSupportRow(row)) classes.push('rollup-support')
  if (isExplicitStatementTotalRow(row) || row.row_type === 'formula' || (row.is_total && row.row_type !== 'account')) classes.push('total')
  else if (row.row_type === 'heading' || row.row_type === 'group') classes.push('group')
  return classes.length ? classes.join(' ') : undefined
}

const rowHasStatementValue = (row: QuickBooksFpaStatementRow) =>
  row.values.some((value) => Math.abs(Number(value || 0)) > 0.005)

const rowHasValuedDescendant = (rows: QuickBooksFpaStatementRow[], rowIndex: number) => {
  const row = rows[rowIndex]
  if (!row) return false
  const rowLevel = Number(row.level || 0)
  for (let index = rowIndex + 1; index < rows.length; index += 1) {
    const candidate = rows[index]
    const candidateLevel = Number(candidate.level || 0)
    if (candidateLevel <= rowLevel) break
    if (rowHasStatementValue(candidate)) return true
  }
  return false
}

const stringSetsEqual = (left: Set<string>, right: Set<string>) => {
  if (left.size !== right.size) return false
  for (const value of left) {
    if (!right.has(value)) return false
  }
  return true
}

const isDetailStatementRow = (row: QuickBooksFpaStatementRow) =>
  row.row_type === 'account' || Boolean(row.account_id) || Boolean(row.account_number)

const displayStatementRowLabel = (row: QuickBooksFpaStatementRow, showAccountCodes: boolean) =>
  showAccountCodes && row.account_number ? `${row.account_number} · ${row.label}` : row.label

const statementSourceNodeId = (nodeId: string) => {
  const sourceMatch = /:source:(.+)$/.exec(nodeId)
  return sourceMatch?.[1] || nodeId
}

const createClientId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  return `fg-cfo-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

const snapshotLayoutNodePatch = (
  node: QuickBooksFpaLayoutNode,
): Partial<QuickBooksFpaLayoutNode> & { id: string; clear_parent?: boolean; clear_cash_flow_section?: boolean } => ({
  id: node.id,
  parent_node_id: node.parent_node_id || undefined,
  clear_parent: !node.parent_node_id,
  statement_type: node.statement_type,
  cash_flow_section: node.cash_flow_section || undefined,
  clear_cash_flow_section: !node.cash_flow_section,
  display_name: node.display_name,
  qbo_account_type: node.qbo_account_type || null,
  qbo_account_sub_type: node.qbo_account_sub_type || null,
  qbo_classification: node.qbo_classification || null,
  normal_balance: node.normal_balance || null,
  include_in_reports: node.include_in_reports,
  sort_order: normalizeQuickBooksFpaSortOrder(node.sort_order),
  formula_json: node.formula_json,
  metadata: node.metadata || null,
})

const stripMetadataKey = (metadata: any, key: string) => {
  if (!metadata || typeof metadata !== 'object') return metadata || null
  const next = { ...metadata }
  const hadKey = Object.prototype.hasOwnProperty.call(next, key)
  delete next[key]
  if (Object.keys(next).length) return next
  return hadKey ? {} : null
}

const stripMergeMetadata = (metadata: any) => {
  return stripMetadataKey(metadata, 'cfoCoreMerge')
}

const stripLayoutEditMetadata = (metadata: any, key: string) => {
  return stripMetadataKey(metadata, key)
}

const invertLayoutPatch = (nodesById: Map<string, QuickBooksFpaLayoutNode>, patch: CfoStatementLayoutPatch): CfoStatementLayoutPatch => {
  const nodes: NonNullable<CfoStatementLayoutPatch['nodes']> = []
  const deleteNodeIds: string[] = []
  for (const nodePatch of patch.nodes || []) {
    const nodeId = String(nodePatch.id || '').trim()
    if (!nodeId) continue
    const node = nodesById.get(nodeId)
    if (node) nodes.push(snapshotLayoutNodePatch(node))
    else deleteNodeIds.push(nodeId)
  }
  return {
    ...(nodes.length ? { nodes } : {}),
    ...(deleteNodeIds.length ? { delete_node_ids: deleteNodeIds } : {}),
  }
}

const layoutPatchNodeIds = (patch: CfoStatementLayoutPatch): string[] => {
  const ids = [
    ...(patch.nodes || []).map((node) => String(node.id || '').trim()),
    ...(patch.delete_node_ids || []).map((id) => String(id || '').trim()),
  ].filter(Boolean)
  return [...new Set(ids)]
}

const layoutChangeFields = (patch: CfoStatementLayoutChangePatch): string[] => {
  const normalized = new Set<string>()
  for (const key of Object.keys(patch || {})) {
    if (key === 'clear_parent') normalized.add('parent_node_id')
    else if (key === 'clear_cash_flow_section') normalized.add('cash_flow_section')
    else normalized.add(key)
  }
  return [...normalized].filter((key) => key !== 'id')
}

const layoutChangeFieldLabel = (field: string): string => {
  if (field === 'display_name') return 'Name'
  if (field === 'statement_type') return 'Statement'
  if (field === 'cash_flow_section') return 'Cash flow'
  if (field === 'parent_node_id') return 'Parent'
  if (field === 'include_in_reports') return 'Hidden'
  if (field === 'sort_order') return 'Order'
  if (field === 'formula_json') return 'Formula'
  if (field === 'metadata') return 'Structure'
  return field.replace(/_/g, ' ')
}

const layoutChangeValue = (
  field: string,
  patch: CfoStatementLayoutChangePatch,
  nodesById: Map<string, QuickBooksFpaLayoutNode>,
): string => {
  if (field === 'parent_node_id') {
    if (patch.clear_parent) return 'Top level'
    const parentId = String(patch.parent_node_id || '').trim()
    return parentId ? (nodesById.get(parentId)?.display_name || 'Row') : 'Top level'
  }
  if (field === 'cash_flow_section') {
    if (patch.clear_cash_flow_section) return 'None'
    return String(patch.cash_flow_section || 'None')
  }
  if (field === 'include_in_reports') return patch.include_in_reports === false ? 'Yes' : 'No'
  if (field === 'formula_json') return patch.formula_json ? 'Formula' : 'None'
  if (field === 'metadata') return patch.metadata ? 'Updated' : 'None'
  const value = (patch as any)[field]
  if (value == null || value === '') return 'None'
  return String(value)
}

const isEmptyLayoutChangePatch = (patch: CfoStatementLayoutChangePatch): boolean =>
  !patch || Object.keys(patch).filter((key) => key !== 'id').length === 0

const normalizeLayoutNodePatchSortOrder = <T extends Partial<QuickBooksFpaLayoutNode> & { id: string }>(patch: T): T => {
  if (!Object.prototype.hasOwnProperty.call(patch, 'sort_order')) return patch
  const numeric = Number(patch.sort_order)
  if (!Number.isFinite(numeric)) {
    const { sort_order: _sortOrder, ...rest } = patch
    return rest as T
  }
  return { ...patch, sort_order: normalizeQuickBooksFpaSortOrder(numeric) }
}

const createLayoutChangeEntry = (
  nodeId: string,
  before: CfoStatementLayoutChangePatch,
  after: CfoStatementLayoutChangePatch,
  options?: {
    id?: string
    createdAt?: string
    actionLabel?: string
    actionSummary?: string
    sourceUndoEntryId?: string
  },
): CfoStatementAppliedLayoutChangeEntry => ({
  id: options?.id || createClientId(),
  nodeId,
  before,
  after,
  createdAt: options?.createdAt || new Date().toISOString(),
  actionLabel: options?.actionLabel,
  actionSummary: options?.actionSummary,
  sourceUndoEntryId: options?.sourceUndoEntryId,
})

const createLayoutBatchChangeEntry = (args: {
  id: string
  nodeId: string
  entries: CfoStatementAppliedLayoutChangeEntry[]
  label?: string
  summary?: string
  createdAt?: string
  sourceUndoEntryId?: string
}): CfoStatementAppliedLayoutChangeEntry => {
  const first = args.entries[0]
  return {
    id: args.id,
    nodeId: args.nodeId || first?.nodeId || '',
    before: first?.before || {},
    after: first?.after || {},
    createdAt: args.createdAt || first?.createdAt || new Date().toISOString(),
    actionLabel: args.label,
    actionSummary: args.summary,
    sourceUndoEntryId: args.sourceUndoEntryId,
    batch: args.entries.map((entry) => ({
      id: entry.id,
      nodeId: entry.nodeId,
      before: entry.before,
      after: entry.after,
    })),
  }
}

const layoutChangePatchRequest = (
  entry: CfoStatementAppliedLayoutChangeEntry,
  side: 'before' | 'after',
): CfoStatementLayoutPatch => {
  const items = entry.batch?.length
    ? entry.batch
    : [{ nodeId: entry.nodeId, before: entry.before, after: entry.after }]
  const nodes: NonNullable<CfoStatementLayoutPatch['nodes']> = []
  const deleteNodeIds: string[] = []
  for (const item of items) {
    const patch = item[side]
    if (side === 'before' && isEmptyLayoutChangePatch(patch)) {
      deleteNodeIds.push(item.nodeId)
    } else {
      nodes.push(normalizeLayoutNodePatchSortOrder({ id: item.nodeId, ...patch }))
    }
  }
  return {
    ...(nodes.length ? { nodes } : {}),
    ...(deleteNodeIds.length ? { delete_node_ids: deleteNodeIds } : {}),
  }
}

const undoEntryToLayoutChangeEntry = (entry: CfoStatementUndoEntry): CfoStatementAppliedLayoutChangeEntry | null => {
  const nodeIds = [...new Set([...layoutPatchNodeIds(entry.after), ...layoutPatchNodeIds(entry.before)])]
  if (!nodeIds.length) return null
  const entries = nodeIds.map((nodeId) => {
    const before = entry.before.nodes?.find((patch) => patch.id === nodeId) || {}
    const after = entry.after.nodes?.find((patch) => patch.id === nodeId) || {}
    return createLayoutChangeEntry(nodeId, before, after, {
      id: `${entry.id}-${nodeId}`,
      createdAt: entry.id,
      sourceUndoEntryId: entry.id,
    })
  })
  if (entries.length === 1) return entries[0]
  return createLayoutBatchChangeEntry({
    id: entry.id,
    nodeId: entries[0].nodeId,
    entries,
    label: entry.label,
    summary: entry.label,
    sourceUndoEntryId: entry.id,
  })
}

const latestLayoutMetadataTimestamp = (rows: QuickBooksFpaLayoutNode[], metadataKey: string, timestampKey: string): string => {
  const latest = rows
    .map((row) => String(row.metadata?.[metadataKey]?.[timestampKey] || '').trim())
    .filter(Boolean)
    .sort()
    .pop()
  return latest || new Date(0).toISOString()
}

const deriveGroupedLayoutChangesFromNodes = (
  nodes: QuickBooksFpaLayoutNode[],
  args: {
    metadataKey: 'cfoCoreMerge' | 'cfoCoreRollup'
    groupMetadataKey: 'cfoCoreUserMergedGroup' | 'cfoCoreUserRollupGroup'
    targetIdKey: 'mergedIntoNodeId' | 'rolledIntoNodeId'
    timestampKey: 'mergedAt' | 'rolledAt'
    createdLabel: string
    movedLabel: (target?: QuickBooksFpaLayoutNode) => string
  },
): CfoStatementAppliedLayoutChangeEntry[] => {
  const byId = new Map(nodes.map((node) => [String(node.id), node]))
  const movedByTarget = new Map<string, QuickBooksFpaLayoutNode[]>()
  for (const node of nodes) {
    const targetId = String(node.metadata?.[args.metadataKey]?.[args.targetIdKey] || '').trim()
    if (!targetId) continue
    if (String(node.parent_node_id || '').trim() !== targetId) continue
    const existing = movedByTarget.get(targetId) || []
    existing.push(node)
    movedByTarget.set(targetId, existing)
  }

  const changes: CfoStatementAppliedLayoutChangeEntry[] = []
  for (const [targetId, movedRows] of movedByTarget) {
    const target = byId.get(targetId)
    const createdGroup = Boolean(target?.metadata?.[args.groupMetadataKey])
    const entries: CfoStatementAppliedLayoutChangeEntry[] = []
    if (createdGroup && target) {
      entries.push(createLayoutChangeEntry(target.id, {}, {
        node_type: target.node_type,
        statement_type: target.statement_type,
        parent_node_id: target.parent_node_id || undefined,
        clear_parent: !target.parent_node_id,
        display_name: target.display_name,
        include_in_reports: target.include_in_reports,
        sort_order: normalizeQuickBooksFpaSortOrder(target.sort_order),
        metadata: target.metadata || null,
      }))
    }
    for (const row of movedRows.sort(compareQuickBooksFpaLayoutNodes)) {
      const metadata = row.metadata?.[args.metadataKey] || {}
      entries.push(createLayoutChangeEntry(row.id, {
        parent_node_id: metadata.previousParentNodeId || undefined,
        clear_parent: !metadata.previousParentNodeId,
        sort_order: normalizeQuickBooksFpaSortOrder(metadata.previousSortOrder, normalizeQuickBooksFpaSortOrder(row.sort_order)),
        metadata: stripLayoutEditMetadata(row.metadata, args.metadataKey),
      }, {
        parent_node_id: row.parent_node_id || undefined,
        clear_parent: !row.parent_node_id,
        sort_order: normalizeQuickBooksFpaSortOrder(row.sort_order),
        metadata: row.metadata || null,
      }))
    }
    if (!entries.length) continue
    const movedNames = movedRows.map((row) => row.display_name || row.qbo_name || 'row').join(', ')
    changes.push(createLayoutBatchChangeEntry({
      id: `cfo-layout-derived-${args.metadataKey}-${targetId}`,
      nodeId: targetId,
      label: createdGroup ? args.createdLabel : args.movedLabel(target),
      summary: createdGroup
        ? `Created ${target?.display_name || 'group'} and moved ${movedRows.length} row(s): ${movedNames}`
        : `Moved ${movedRows.length} row(s) into ${target?.display_name || 'group'}: ${movedNames}`,
      entries,
      createdAt: latestLayoutMetadataTimestamp(movedRows, args.metadataKey, args.timestampKey),
    }))
  }
  return changes
}

const deriveAppliedLayoutChangesFromNodes = (nodes: QuickBooksFpaLayoutNode[]): CfoStatementAppliedLayoutChangeEntry[] => {
  const groupedChanges = [
    ...deriveGroupedLayoutChangesFromNodes(nodes, {
      metadataKey: 'cfoCoreMerge',
      groupMetadataKey: 'cfoCoreUserMergedGroup',
      targetIdKey: 'mergedIntoNodeId',
      timestampKey: 'mergedAt',
      createdLabel: 'Merge accounts',
      movedLabel: (target) => `Merge into ${target?.display_name || 'group'}`,
    }),
    ...deriveGroupedLayoutChangesFromNodes(nodes, {
      metadataKey: 'cfoCoreRollup',
      groupMetadataKey: 'cfoCoreUserRollupGroup',
      targetIdKey: 'rolledIntoNodeId',
      timestampKey: 'rolledAt',
      createdLabel: 'Roll up accounts',
      movedLabel: (target) => `Roll up into ${target?.display_name || 'group'}`,
    }),
  ]
  return groupedChanges.sort((a, b) => String(a.createdAt || '').localeCompare(String(b.createdAt || '')))
}

const layoutChangeEntryNodeIdSet = (entries: CfoStatementAppliedLayoutChangeEntry[]): Set<string> => {
  const ids = new Set<string>()
  for (const entry of entries) {
    ids.add(entry.nodeId)
    for (const item of entry.batch || []) ids.add(item.nodeId)
  }
  return ids
}

const layoutChangeTouchesAnyNode = (entry: CfoStatementAppliedLayoutChangeEntry, nodeIds: Set<string>): boolean => {
  if (!nodeIds.size) return false
  if (nodeIds.has(entry.nodeId)) return true
  return Boolean(entry.batch?.some((item) => nodeIds.has(item.nodeId)))
}

const layoutChangeRowLabel = (
  entry: CfoStatementAppliedLayoutChangeEntry,
  nodesById: Map<string, QuickBooksFpaLayoutNode>,
): string => {
  const actionLabel = String(entry.actionLabel || '').trim()
  if (actionLabel) return actionLabel
  const currentName = String(nodesById.get(entry.nodeId)?.display_name || '').trim()
  const afterName = String(entry.after.display_name || '').trim()
  const beforeName = String(entry.before.display_name || '').trim()
  return currentName || afterName || beforeName || 'Layout row'
}

const layoutChangeSummary = (
  entry: CfoStatementAppliedLayoutChangeEntry,
  nodesById: Map<string, QuickBooksFpaLayoutNode>,
): string => {
  const actionSummary = String(entry.actionSummary || '').trim()
  if (actionSummary) return actionSummary
  const fields = layoutChangeFields(entry.after).slice(0, 3)
  if (!fields.length) return 'Updated row'
  return fields.map((field) => {
    const before = layoutChangeValue(field, entry.before, nodesById)
    const after = layoutChangeValue(field, entry.after, nodesById)
    return `${layoutChangeFieldLabel(field)}: ${before} -> ${after}`
  }).join(' | ')
}

const firstEditableNode = (
  rows: QuickBooksFpaStatementRow[],
  nodesById: Map<string, QuickBooksFpaLayoutNode>,
) => {
  for (const row of rows) {
    const node = nodesById.get(statementSourceNodeId(row.node_id))
    if (node) return node
  }
  return null
}

const normalizedStatementLabel = (value: string | null | undefined) =>
  String(value || '').trim().toLowerCase().replace(/\s+/g, ' ')

const aggregateRowValue = (row: QuickBooksFpaStatementRow, indices: number[], statementType: string) => {
  return statementSectionTotalValue(row, indices, /balance_sheet/i.test(statementType))
}

const findExplicitTotalRow = (rows: QuickBooksFpaStatementRow[], row: QuickBooksFpaStatementRow) =>
  resolveStatementSectionTotal(rows, row).total

const rowHasStatementActivity = (rows: QuickBooksFpaStatementRow[], rowIndex: number) => {
  const row = rows[rowIndex]
  if (!row) return false
  const explicitTotal = findExplicitTotalRow(rows, row)
  return Boolean(rowHasStatementValue(row) || (explicitTotal && rowHasStatementValue(explicitTotal)) || rowHasValuedDescendant(rows, rowIndex))
}

const rowHasDisplayedActivity = (
  rows: QuickBooksFpaStatementRow[],
  rowIndex: number,
  columns: CfoStatementColumn[],
) => {
  const row = rows[rowIndex]
  if (!row || !columns.length) return false
  const hasColumnActivity = (candidate: QuickBooksFpaStatementRow) =>
    columns.some((column) => {
      const value = column.read(candidate)
      return value != null && Number.isFinite(value) && Math.abs(value) >= 0.005
    })
  if (hasColumnActivity(row)) return true
  const explicitTotal = findExplicitTotalRow(rows, row)
  if (explicitTotal && hasColumnActivity(explicitTotal)) return true
  const rowLevel = Number(row.level || 0)
  for (let index = rowIndex + 1; index < rows.length; index += 1) {
    const candidate = rows[index]
    if (Number(candidate.level || 0) <= rowLevel) break
    if (hasColumnActivity(candidate)) return true
  }
  return false
}

const aggregateRowValueWithChildren = (
  rows: QuickBooksFpaStatementRow[],
  row: QuickBooksFpaStatementRow,
  indices: number[],
  statementType: string,
) => {
  const section = resolveStatementSectionTotal(rows, row)
  if (section.authoritative && section.total) {
    return statementSectionTotalValue(section.total, indices, /balance_sheet/i.test(statementType))
  }
  const direct = aggregateRowValue(row, indices, statementType)
  // These rows are already projected controls. Zero and unavailable vectors
  // cannot be replaced by their display children or duplicate totals.
  if (row.node_id.startsWith('fg-cfo-pnl-rollup-') && !row.node_id.includes(':source:')) return direct
  if (direct != null && Math.abs(direct) >= 0.005) return direct
  if (row.row_type !== 'heading' && row.row_type !== 'group') return direct
  const explicitTotal = findExplicitTotalRow(rows, row)
  if (explicitTotal) {
    const totalValue = aggregateRowValue(explicitTotal, indices, statementType)
    if (totalValue != null && Math.abs(totalValue) >= 0.005) return totalValue
  }
  const rowIndex = rows.findIndex((candidate) => candidate.node_id === row.node_id)
  if (rowIndex < 0) return direct
  const rowLevel = Number(row.level || 0)
  let total = 0
  let foundChild = false
  for (let index = rowIndex + 1; index < rows.length; index += 1) {
    const child = rows[index]
    if (Number(child.level || 0) <= rowLevel) break
    if (Number(child.level || 0) !== rowLevel + 1) continue
    const value = aggregateRowValueWithChildren(rows, child, indices, statementType)
    if (value == null) continue
    total += value
    foundChild = true
  }
  return foundChild ? total : direct
}

const formatDeltaPct = (value: number | null) => {
  if (value == null || !Number.isFinite(value)) return '—'
  const sign = value > 0 ? '+' : value < 0 ? '−' : ''
  return `${sign}${Math.abs(value * 100).toFixed(1)}%`
}

type StatementCurrencyScale = 'ones' | 'thousands' | 'millions'

const formatScaledAmount = (value: number, divisor: number, suffix: string) => {
  const scaled = value / divisor
  const fractionDigits = suffix === 'K' ? 1 : Math.abs(scaled) < 1 ? 2 : 1
  return `${cfoCurrencySymbol()}${scaled.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: fractionDigits,
  })}${suffix}`
}

const formatStatementCurrency = (value: number | null, scale: StatementCurrencyScale = 'thousands') => {
  if (value == null || !Number.isFinite(value)) return '—'
  if (Math.abs(value) < 0.005) return '—'
  const abs = Math.abs(value)
  const formatted =
    scale === 'millions'
      ? formatScaledAmount(abs, 1_000_000, 'M')
      : scale === 'thousands'
        ? formatScaledAmount(abs, 1_000, 'K')
        : `${cfoCurrencySymbol()}${abs.toLocaleString(undefined, { maximumFractionDigits: abs >= 100 ? 0 : 1 })}`
  return value < 0 ? `(${formatted})` : formatted
}

const formatStatementPercentOfSales = (value: number | null, delta = false) => {
  if (value == null || !Number.isFinite(value)) return '—'
  if (Math.abs(value) < 0.00005) return '—'
  const sign = value > 0 && delta ? '+' : value < 0 ? '−' : ''
  return `${sign}${Math.abs(value * 100).toFixed(1)}${delta ? ' pts' : '%'}`
}

const formatStatementClipboardValue = (
  column: CfoStatementColumn,
  row: QuickBooksFpaStatementRow,
  currencyScale: StatementCurrencyScale,
  percentOfSales: boolean,
) => {
  const value = column.read(row)
  if (column.kind === 'pct') return formatDeltaPct(value)
  if (percentOfSales) return formatStatementPercentOfSales(column.readPctOfSales?.(row) ?? null, column.kind === 'delta')
  return formatStatementCurrency(value, currencyScale)
}

const copyTextToClipboard = async (text: string): Promise<void> => {
  if (!text) return
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text)
    return
  }
  if (typeof document === 'undefined') return
  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.setAttribute('readonly', 'true')
  textarea.style.position = 'fixed'
  textarea.style.left = '-9999px'
  textarea.style.top = '0'
  document.body.appendChild(textarea)
  textarea.select()
  document.execCommand('copy')
  textarea.remove()
}

const statementCurrencyScale = (rows: QuickBooksFpaStatementRow[], columns: CfoStatementColumn[]): StatementCurrencyScale => {
  const values = rows.flatMap((row) =>
    columns
      .filter((column) => column.kind !== 'pct')
      .map((column) => Math.abs(Number(column.read(row) || 0)))
      .filter((value) => Number.isFinite(value) && value >= 0.005),
  )
  if (!values.length) return 'thousands'
  const millionCount = values.filter((value) => value >= 1_000_000).length
  const thousandCount = values.filter((value) => value >= 1_000).length
  if (millionCount >= Math.ceil(values.length * 0.6)) return 'millions'
  if (thousandCount >= Math.ceil(values.length * 0.6)) return 'thousands'
  return 'ones'
}

const zeroStatementValues = (periodCount: number) => Array.from({ length: periodCount }, () => 0)

const statementValues = (row: QuickBooksFpaStatementRow | null | undefined, periodCount: number) =>
  Array.from({ length: periodCount }, (_, index) => Number(row?.values?.[index] || 0))

const statementValuesHaveAmount = (values: number[]) =>
  values.some((value) => Number.isFinite(value) && Math.abs(value) >= 0.005)

const addStatementValues = (...vectors: number[][]) => {
  const length = Math.max(0, ...vectors.map((vector) => vector.length))
  return Array.from({ length }, (_, index) => vectors.reduce((sum, vector) => sum + Number(vector[index] || 0), 0))
}

const subtractStatementValues = (left: number[], right: number[]) =>
  Array.from({ length: Math.max(left.length, right.length) }, (_, index) => Number(left[index] || 0) - Number(right[index] || 0))

const fillBlankStatementValues = (primary: number[], fallback: number[]) =>
  Array.from({ length: Math.max(primary.length, fallback.length) }, (_, index) => {
    const value = Number(primary[index] || 0)
    if (Math.abs(value) >= 0.005) return value
    return Number(fallback[index] || 0)
  })

const findStatementRowByLabel = (rows: QuickBooksFpaStatementRow[], labels: string[]) => {
  const targets = new Set(labels.map(normalizedStatementLabel))
  return rows.find((row) => isCanonicalProfitAndLossControl(row) && targets.has(normalizedStatementLabel(row.label)))
    || rows.find((row) => targets.has(normalizedStatementLabel(row.label))) || null
}

type ProfitAndLossRollupBucket = 'income' | 'costOfGoodsSold' | 'expenses' | 'otherIncome' | 'otherExpenses'

const profitAndLossRollupBucket = (row: QuickBooksFpaStatementRow): ProfitAndLossRollupBucket | null => {
  const label = normalizedStatementLabel(row.label)
  const physicalAccount = Boolean(row.account_id) || row.row_type === 'account' || row.row_type === 'line'
  if (!physicalAccount && /^(total\s+)?(gross profit|operating income|net operating income|net income|net earnings|ebitda)$/.test(label)) return null
  const accountType = normalizedStatementLabel(row.qbo_account_type)
  const accountSubType = normalizedStatementLabel(row.qbo_account_sub_type)
  const classification = normalizedStatementLabel(row.qbo_classification)
  const typed = `${accountType} ${accountSubType} ${classification}`.replace(/[\s_-]+/g, '')
  if (typed.includes('costofgoods') || typed.includes('costofsales')) return 'costOfGoodsSold'
  if (typed.includes('otherincome')) return 'otherIncome'
  if (typed.includes('otherexpense')) return 'otherExpenses'
  if (accountType === 'income' || accountType === 'revenue' || classification === 'revenue' || classification === 'income') return 'income'
  if (accountType === 'expense' || classification === 'expense') return 'expenses'
  const accountNumber = String(row.account_number || row.label?.match(/^\s*(\d{4,})/)?.[1] || '')
  if (/^4/.test(accountNumber)) return 'income'
  if (/^5/.test(accountNumber)) return 'costOfGoodsSold'
  if (/^[67]/.test(accountNumber)) return 'expenses'
  if (/^8/.test(accountNumber)) {
    return label.includes('income') || label.includes('earned') ? 'otherIncome' : 'otherExpenses'
  }
  return null
}

const aggregateProfitAndLossBucketValues = (
  rows: QuickBooksFpaStatementRow[],
  bucket: ProfitAndLossRollupBucket,
  periodCount: number,
) => {
  const parentIds = new Set(rows.map((row) => row.parent_node_id).filter((id): id is string => Boolean(id)))
  const sourceRows = rows.filter((row) => {
    if (parentIds.has(row.node_id)) return false
    if (isExplicitStatementTotalRow(row)) return false
    const rowType = normalizedStatementLabel(row.row_type)
    if (rowType === 'formula') return false
    if ((rowType === 'heading' || rowType === 'group') && !profitAndLossRollupBucket(row)) return false
    return profitAndLossRollupBucket(row) === bucket
  })
  return addStatementValues(...sourceRows.map((row) => statementValues(row, periodCount)))
}

const aggregateProfitAndLossRowsByLabel = (
  rows: QuickBooksFpaStatementRow[],
  periodCount: number,
  matches: (label: string) => boolean,
) => addStatementValues(...rows
  .filter((row) => {
    if (isExplicitStatementTotalRow(row)) return false
    const values = statementValues(row, periodCount)
    return statementValuesHaveAmount(values) && matches(normalizedStatementLabel(row.label))
  })
  .map((row) => statementValues(row, periodCount)))

const statementValuesOrBucketValues = (
  row: QuickBooksFpaStatementRow | null | undefined,
  rows: QuickBooksFpaStatementRow[],
  bucket: ProfitAndLossRollupBucket,
  periodCount: number,
) => {
  const direct = statementValues(row, periodCount)
  if (row && isCanonicalProfitAndLossControl(row)) return direct
  const bucketValues = aggregateProfitAndLossBucketValues(rows, bucket, periodCount)
  if (!statementValuesHaveAmount(direct)) return bucketValues
  if (!statementValuesHaveAmount(bucketValues)) return direct
  return fillBlankStatementValues(direct, bucketValues)
}

const profitAndLossRollupRow = (
  source: QuickBooksFpaStatementRow | null | undefined,
  args: { nodeId: string; label: string; values: Array<number | null>; rowType?: string; parentNodeId?: string | null; level?: number },
): QuickBooksFpaStatementRow => ({
  ...(source || {
    statement_type: 'profit_and_loss',
    account_id: null,
    account_number: null,
    cash_flow_section: null,
    qbo_account_type: null,
    qbo_account_sub_type: null,
    qbo_classification: null,
    normal_balance: null,
  }),
  node_id: args.nodeId,
  parent_node_id: args.parentNodeId || null,
  row_type: args.rowType || source?.row_type || 'formula',
  statement_type: 'profit_and_loss',
  label: args.label,
  level: args.level || 0,
  values: source && isCanonicalProfitAndLossControl(source)
    && (args.nodeId !== 'fg-cfo-pnl-rollup-other' || source.node_id === 'fpa:pnl:net_other_income')
    ? [...source.values] : args.values,
  is_total: true,
  is_formula: args.rowType === 'formula' || Boolean(source?.is_formula),
})

const ebitdaAddbackGroupId = (row: QuickBooksFpaStatementRow) => row.is_total ? null : ebitdaAddbackGroup(row)

const EBITDA_ADDBACK_LABELS: Record<NonNullable<ReturnType<typeof ebitdaAddbackGroupId>>, string> = {
  interest: 'Interest Expense',
  taxes: 'Income Taxes',
  depreciation: 'Depreciation',
  amortization: 'Amortization',
}

const EBITDA_ADDBACK_ORDER: Array<NonNullable<ReturnType<typeof ebitdaAddbackGroupId>>> = ['interest', 'taxes', 'depreciation', 'amortization']

const PROFIT_AND_LOSS_ROLLUP_PARENT_IDS = [
  'fg-cfo-pnl-rollup-income',
  'fg-cfo-pnl-rollup-cogs',
  'fg-cfo-pnl-rollup-gross-profit',
  'fg-cfo-pnl-rollup-expenses',
  'fg-cfo-pnl-rollup-operating-income',
  'fg-cfo-pnl-rollup-other',
  'fg-cfo-pnl-rollup-other:child:0',
  'fg-cfo-pnl-rollup-other:child:1',
  'fg-cfo-pnl-rollup-net-income',
  'fg-cfo-pnl-rollup-ebitda',
]

const addProfitAndLossRollupParentIds = (ids: Set<string>) => {
  for (const id of PROFIT_AND_LOSS_ROLLUP_PARENT_IDS) ids.add(id)
  return ids
}

const sourceChildrenBetween = (
  section: ReturnType<typeof selectProfitAndLossSection>,
  parentNodeId: string,
): QuickBooksFpaStatementRow[] => {
  if (!section.heading) return []
  const startLevel = Number(section.heading.level || 0)
  const out: QuickBooksFpaStatementRow[] = []
  const syntheticNodeIdBySourceId = new Map<string, string>()
  for (const row of section.details) {
    if (isExplicitStatementTotalRow(row)) continue
    const nodeId = `${parentNodeId}:source:${row.node_id}`
    syntheticNodeIdBySourceId.set(row.node_id, nodeId)
    out.push({
      ...row,
      node_id: nodeId,
      parent_node_id: row.parent_node_id ? syntheticNodeIdBySourceId.get(row.parent_node_id) || parentNodeId : parentNodeId,
      level: Math.max(1, Number(row.level || 0) - startLevel),
    })
  }
  return out
}

const sourceChildrenForProfitAndLossBucket = (
  rows: QuickBooksFpaStatementRow[],
  bucket: ProfitAndLossRollupBucket,
  parentNodeId: string,
  level: number,
): QuickBooksFpaStatementRow[] => {
  const sourceRows = rows.filter((row) => {
    if (isExplicitStatementTotalRow(row)) return false
    if (normalizedStatementLabel(row.row_type) === 'formula') return false
    return profitAndLossRollupBucket(row) === bucket
  })
  const sourceRowsById = new Map(sourceRows.map((row) => [row.node_id, row]))
  return sourceRows.map((row) => {
    const sourceParent = row.parent_node_id && sourceRowsById.get(row.parent_node_id)
    let depth = level
    let parent = sourceParent
    const visited = new Set([row.node_id])
    while (parent && !visited.has(parent.node_id)) {
      visited.add(parent.node_id)
      depth += 1
      parent = parent.parent_node_id && sourceRowsById.get(parent.parent_node_id)
    }
    return {
      ...row,
      node_id: `${parentNodeId}:source:${row.node_id}`,
      parent_node_id: sourceParent ? `${parentNodeId}:source:${sourceParent.node_id}` : parentNodeId,
      level: depth,
    }
  })
}

const childRollupRow = (
  parentNodeId: string,
  source: QuickBooksFpaStatementRow | null | undefined,
  label: string,
  values: number[],
  index: number,
): QuickBooksFpaStatementRow =>
  profitAndLossRollupRow(source, {
    nodeId: `${parentNodeId}:child:${index}`,
    parentNodeId,
    label,
    values,
    rowType: source?.row_type === 'group' ? 'formula' : source?.row_type || 'formula',
    level: 1,
  })

const formulaTotalRollupRow = (
  source: QuickBooksFpaStatementRow | null | undefined,
  args: { nodeId: string; label: string; values: number[] },
): QuickBooksFpaStatementRow =>
  profitAndLossRollupRow(source, {
    nodeId: args.nodeId,
    label: args.label,
    values: args.values,
    rowType: 'group',
    level: 0,
  })

const isProfitAndLossBoundaryTotalRow = (row: QuickBooksFpaStatementRow) =>
  row.node_id === 'fg-cfo-pnl-rollup-total-income' ||
  row.node_id === 'fg-cfo-pnl-rollup-total-cogs' ||
  row.node_id === 'fg-cfo-pnl-rollup-total-expenses'

const isBlankDuplicateProfitAndLossRollupRow = (
  row: QuickBooksFpaStatementRow,
  rows: QuickBooksFpaStatementRow[],
) => {
  if (row.node_id === 'fg-cfo-pnl-rollup-cogs') return false
  const label = normalizedStatementLabel(row.label)
  if (label !== 'cost of goods sold' && label !== 'cost of sales') return false
  if (statementValuesHaveAmount(statementValues(row, row.values.length))) return false
  return rows.some((candidate) => {
    if (candidate.node_id === row.node_id) return false
    const candidateLabel = normalizedStatementLabel(candidate.label)
    if (!candidateLabel.includes(label)) return false
    return statementValuesHaveAmount(statementValues(candidate, candidate.values.length))
  })
}

const buildProfitAndLossCollapsedRollupRows = (rows: QuickBooksFpaStatementRow[], periodCount: number): QuickBooksFpaStatementRow[] => {
  const incomeSection = selectProfitAndLossSection(rows, 'income')
  const costSection = selectProfitAndLossSection(rows, 'costOfGoodsSold')
  const expenseSection = selectProfitAndLossSection(rows, 'expenses')
  const otherIncomeSection = selectProfitAndLossSection(rows, 'otherIncome')
  const otherExpenseSection = selectProfitAndLossSection(rows, 'otherExpenses')
  const income = incomeSection.total || incomeSection.heading
  const costOfGoodsSold = costSection.total || costSection.heading
  const grossProfit = findStatementRowByLabel(rows, ['Gross Profit'])
  const expenses = expenseSection.total || expenseSection.heading
  const operatingIncome = findStatementRowByLabel(rows, ['Net Operating Income', 'Operating Income'])
  const otherIncome = otherIncomeSection.total || otherIncomeSection.heading
  const otherExpenses = otherExpenseSection.total || otherExpenseSection.heading
  const netOtherIncome = findStatementRowByLabel(rows, ['Net Other Income', 'Other Income & Expenses', 'Other Income and Expenses'])
  const netIncome = findStatementRowByLabel(rows, ['Net Income', 'Net Earnings'])
  const incomeValues = statementValuesOrBucketValues(income, rows, 'income', periodCount)
  let costOfGoodsSoldValues = statementValuesOrBucketValues(costOfGoodsSold, rows, 'costOfGoodsSold', periodCount)
  let expenseValues = statementValuesOrBucketValues(expenses, rows, 'expenses', periodCount)
  if (!(costOfGoodsSold && isCanonicalProfitAndLossControl(costOfGoodsSold)) && !statementValuesHaveAmount(costOfGoodsSoldValues)) {
    costOfGoodsSoldValues = aggregateProfitAndLossRowsByLabel(
      rows,
      periodCount,
      (label) => label.includes('cost of goods sold') || label.includes('cost of sales'),
    )
  }
  const otherIncomeDirectValues = statementValuesOrBucketValues(otherIncome, rows, 'otherIncome', periodCount)
  const otherExpenseDirectValues = statementValuesOrBucketValues(otherExpenses, rows, 'otherExpenses', periodCount)
  const netOtherIncomeDirectValues = statementValues(netOtherIncome, periodCount)
  const otherIncomeValues = statementValuesHaveAmount(netOtherIncomeDirectValues)
    ? netOtherIncomeDirectValues
    : subtractStatementValues(otherIncomeDirectValues, otherExpenseDirectValues)
  const addbackValuesByGroup = new Map(EBITDA_ADDBACK_ORDER.map((groupId) => [groupId, zeroStatementValues(periodCount)]))
  for (const row of rows) {
    const groupId = ebitdaAddbackGroupId(row)
    if (!groupId) continue
    addbackValuesByGroup.set(groupId, addStatementValues(addbackValuesByGroup.get(groupId) || zeroStatementValues(periodCount), statementValues(row, periodCount)))
  }
  const derivedGrossProfitValues = subtractStatementValues(incomeValues, costOfGoodsSoldValues)
  const grossProfitValues = grossProfit && isCanonicalProfitAndLossControl(grossProfit)
    ? statementValues(grossProfit, periodCount)
    : statementValuesHaveAmount(statementValues(grossProfit, periodCount))
      ? fillBlankStatementValues(statementValues(grossProfit, periodCount), derivedGrossProfitValues)
      : derivedGrossProfitValues
  if (!(costOfGoodsSold && isCanonicalProfitAndLossControl(costOfGoodsSold)) && !statementValuesHaveAmount(costOfGoodsSoldValues) && statementValuesHaveAmount(incomeValues) && statementValuesHaveAmount(grossProfitValues)) {
    costOfGoodsSoldValues = subtractStatementValues(incomeValues, grossProfitValues)
  }
  const operatingIncomeDirectValues = statementValues(operatingIncome, periodCount)
  const derivedOperatingIncomeValues = subtractStatementValues(grossProfitValues, expenseValues)
  const operatingIncomeValues = operatingIncome && isCanonicalProfitAndLossControl(operatingIncome)
    ? operatingIncomeDirectValues
    : statementValuesHaveAmount(operatingIncomeDirectValues)
      ? fillBlankStatementValues(operatingIncomeDirectValues, derivedOperatingIncomeValues)
      : derivedOperatingIncomeValues
  if (!(expenses && isCanonicalProfitAndLossControl(expenses)) && !statementValuesHaveAmount(expenseValues) && statementValuesHaveAmount(grossProfitValues) && statementValuesHaveAmount(operatingIncomeValues)) {
    expenseValues = subtractStatementValues(grossProfitValues, operatingIncomeValues)
  }
  const derivedNetIncomeValues = addStatementValues(operatingIncomeValues, otherIncomeValues)
  const netIncomeDirectValues = statementValues(netIncome, periodCount)
  const netIncomeValues = netIncome && isCanonicalProfitAndLossControl(netIncome)
    ? netIncomeDirectValues
    : statementValuesHaveAmount(netIncomeDirectValues) ? fillBlankStatementValues(netIncomeDirectValues, derivedNetIncomeValues) : derivedNetIncomeValues
  const ebitdaAddbacks = EBITDA_ADDBACK_ORDER.map((groupId) => addbackValuesByGroup.get(groupId) || zeroStatementValues(periodCount))
  const ebitdaValues = addStatementValues(netIncomeValues, ...ebitdaAddbacks)
    .map((value, index) => netIncome && isCanonicalProfitAndLossControl(netIncome)
      && netIncome.values[index] == null ? null : value)
  const incomeNodeId = 'fg-cfo-pnl-rollup-income'
  const costOfGoodsSoldNodeId = 'fg-cfo-pnl-rollup-cogs'
  const grossProfitNodeId = 'fg-cfo-pnl-rollup-gross-profit'
  const expensesNodeId = 'fg-cfo-pnl-rollup-expenses'
  const operatingIncomeNodeId = 'fg-cfo-pnl-rollup-operating-income'
  const otherNodeId = 'fg-cfo-pnl-rollup-other'
  const netIncomeNodeId = 'fg-cfo-pnl-rollup-net-income'
  const ebitdaNodeId = 'fg-cfo-pnl-rollup-ebitda'
  const incomeChildren = sourceChildrenBetween(incomeSection, incomeNodeId)
  const costChildren = sourceChildrenBetween(costSection, costOfGoodsSoldNodeId)
  const expenseChildren = sourceChildrenBetween(expenseSection, expensesNodeId)
  const resolvedIncomeChildren = incomeChildren.length ? incomeChildren : sourceChildrenForProfitAndLossBucket(rows, 'income', incomeNodeId, 1)
  const resolvedCostChildren = costChildren.length ? costChildren : sourceChildrenForProfitAndLossBucket(rows, 'costOfGoodsSold', costOfGoodsSoldNodeId, 1)
  const resolvedExpenseChildren = expenseChildren.length ? expenseChildren : sourceChildrenForProfitAndLossBucket(rows, 'expenses', expensesNodeId, 1)
  const incomeTotalRow = formulaTotalRollupRow(income, { nodeId: 'fg-cfo-pnl-rollup-total-income', label: 'Total Income', values: incomeValues })
  const costTotalRow = formulaTotalRollupRow(costOfGoodsSold, { nodeId: 'fg-cfo-pnl-rollup-total-cogs', label: 'Total Cost of Goods Sold', values: costOfGoodsSoldValues })
  const expenseTotalRow = formulaTotalRollupRow(expenses, { nodeId: 'fg-cfo-pnl-rollup-total-expenses', label: 'Total Expenses', values: expenseValues })
  const otherIncomeNodeId = `${otherNodeId}:child:0`
  const otherExpensesNodeId = `${otherNodeId}:child:1`
  const otherChildren = [
    childRollupRow(otherNodeId, otherIncome, 'Other Income', otherIncomeDirectValues, 0),
    ...sourceChildrenForProfitAndLossBucket(rows, 'otherIncome', otherIncomeNodeId, 2),
    childRollupRow(otherNodeId, otherExpenses, 'Other Expenses', otherExpenseDirectValues, 1),
    ...sourceChildrenForProfitAndLossBucket(rows, 'otherExpenses', otherExpensesNodeId, 2),
  ]
  const rollupRows = [
    profitAndLossRollupRow(income, { nodeId: incomeNodeId, label: 'Income', values: incomeValues, rowType: 'group' }),
    ...resolvedIncomeChildren,
    incomeTotalRow,
    profitAndLossRollupRow(costOfGoodsSold, { nodeId: costOfGoodsSoldNodeId, label: 'Cost of Goods Sold', values: costOfGoodsSoldValues, rowType: 'group' }),
    ...resolvedCostChildren,
    costTotalRow,
    profitAndLossRollupRow(grossProfit, { nodeId: grossProfitNodeId, label: 'Gross Profit', values: grossProfitValues, rowType: 'formula' }),
    profitAndLossRollupRow(expenses, { nodeId: expensesNodeId, label: 'Expenses', values: expenseValues, rowType: 'group' }),
    ...resolvedExpenseChildren,
    profitAndLossRollupRow(operatingIncome, { nodeId: operatingIncomeNodeId, label: 'Operating Income', values: operatingIncomeValues, rowType: 'formula' }),
    profitAndLossRollupRow(netOtherIncome || otherIncome || otherExpenses, { nodeId: otherNodeId, label: 'Other Income & Expenses', values: otherIncomeValues, rowType: 'formula' }),
    ...otherChildren,
    expenseTotalRow,
    profitAndLossRollupRow(netIncome, { nodeId: netIncomeNodeId, label: 'Net Income', values: netIncomeValues, rowType: 'formula' }),
    profitAndLossRollupRow(null, { nodeId: ebitdaNodeId, label: 'EBITDA', values: ebitdaValues, rowType: 'formula' }),
    profitAndLossRollupRow(netIncome, { nodeId: `${ebitdaNodeId}:net-income`, parentNodeId: ebitdaNodeId, label: 'Net Income', values: netIncomeValues, rowType: 'formula', level: 1 }),
    ...EBITDA_ADDBACK_ORDER
      .map((groupId) => {
        const values = addbackValuesByGroup.get(groupId) || zeroStatementValues(periodCount)
        if (!values.some((value) => Math.abs(value) > 0.005)) return null
        return profitAndLossRollupRow(null, {
          nodeId: `${ebitdaNodeId}:${groupId}`,
          parentNodeId: ebitdaNodeId,
          label: EBITDA_ADDBACK_LABELS[groupId],
          values,
          rowType: 'formula',
          level: 1,
        })
      })
      .filter((row): row is QuickBooksFpaStatementRow => Boolean(row)),
  ]
  return rollupRows
}

const buildColumns = (
  statement: QuickBooksFpaStatementRender,
  endPeriod: string | null | undefined,
  comparisonMode: CfoStatementComparisonMode,
  maxPeriods: number,
  periodHeaderFormatter?: (period: string | null | undefined, index: number) => ReactNode,
  periodWindow?: CfoStatementColumnWindow,
): CfoStatementColumn[] => {
  const statementType = statement.statement_type || ''
  const revenueRows = statement.rows.filter((row) => /^(total\s+)?(income|revenue|sales)$/i.test(normalizedStatementLabel(row.label)))
  const revenueCandidateScore = (row: QuickBooksFpaStatementRow, value: number) => {
    const label = normalizedStatementLabel(row.label)
    let score = Math.abs(value)
    if (/^(total\s+)?(income|revenue)$/.test(label)) score += 10_000_000_000
    if (row.row_type === 'group' || row.row_type === 'formula' || row.is_total) score += 1_000_000_000
    if (Number(row.level || 0) === 0) score += 1_000_000_000
    if (/^total\s+/.test(label) || row.is_total) score += 100_000_000
    return score
  }
  const pctOfSales = (row: QuickBooksFpaStatementRow, indices: number[]) => {
    const revenueRow = statement.rows.find((candidate) => candidate.node_id === 'fpa:pnl:total_revenue'
      || candidate.node_id === 'fg-cfo-pnl-rollup-income') || revenueRows
      .map((candidate) => {
        const value = aggregateRowValueWithChildren(statement.rows, candidate, indices, statementType)
        return value != null && Math.abs(value) >= 0.005
          ? { row: candidate, value, score: revenueCandidateScore(candidate, value) }
          : null
      })
      .filter((candidate): candidate is { row: QuickBooksFpaStatementRow; value: number; score: number } => Boolean(candidate))
      .sort((left, right) => right.score - left.score)[0]?.row || null
    if (!revenueRow) return null
    const numerator = aggregateRowValueWithChildren(statement.rows, row, indices, statementType)
    const denominator = aggregateRowValueWithChildren(statement.rows, revenueRow, indices, statementType)
    if (numerator == null || denominator == null || Math.abs(denominator) < 0.005) return null
    return numerator / denominator
  }
  return buildCfoStatementColumnSpecs(statement, endPeriod, comparisonMode, maxPeriods, periodWindow).map((spec): CfoStatementColumn => {
    const currentIndices = spec.currentIndices || []
    const priorIndices = spec.priorIndices || []
    const readValue = (row: QuickBooksFpaStatementRow, indices: number[]) =>
      aggregateRowValueWithChildren(statement.rows, row, indices, statementType)
    const read = (row: QuickBooksFpaStatementRow): number | null => {
      if (spec.kind === 'value') return readValue(row, spec.indices || [])
      const currentValue = readValue(row, currentIndices)
      const priorValue = readValue(row, priorIndices)
      if (currentValue == null || priorValue == null) return null
      if (spec.kind === 'pct') return Math.abs(priorValue) < 0.005 ? null : (currentValue - priorValue) / Math.abs(priorValue)
      return currentValue - priorValue
    }
    const readPctOfSales = spec.kind === 'pct' ? undefined : (row: QuickBooksFpaStatementRow): number | null => {
      if (spec.kind === 'value') return pctOfSales(row, spec.indices || [])
      const currentPct = pctOfSales(row, currentIndices)
      const priorPct = pctOfSales(row, priorIndices)
      if (currentPct == null || priorPct == null) return null
      return currentPct - priorPct
    }
    return {
      key: spec.key,
      header: spec.periodIndex != null && periodHeaderFormatter
        ? periodHeaderFormatter(spec.period || null, spec.periodIndex)
        : spec.label,
      headerLabel: spec.label,
      kind: spec.kind,
      period: spec.period,
      periodIndex: spec.periodIndex,
      read,
      readPctOfSales,
    }
  })
}

export function CfoStatementTable({
  statement,
  percentOfSales = false,
  showCurrencyAndPercent = false,
  endPeriod = null,
  comparisonMode = 'rolling_12',
  maxPeriods = 12,
  periodWindow,
  maxRows = 80,
  defaultCollapsed = false,
  defaultRollupDepth = 0,
  rollupDepth: controlledRollupDepth,
  onRollupDepthChange,
  fullscreen = false,
  periodHeaderFormatter,
  toolbarLeading = null,
  toolbarActions = null,
  layoutName = null,
  onOpenGlDetail,
  onExplainCell,
  onAnalyzeRow,
  layoutEdit = null,
  layoutNodes = null,
  loading = false,
  loadingLabel = 'Loading statement detail…',
  emptyLabel = 'No statement detail to show yet. It appears here once this workbook’s reports finish building.',
  hideBlankRows = true,
  cellDecorator,
  rowBadge,
  focusNodeId = null,
  focusNonce = 0,
}: CfoStatementTableProps) {
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [advancedPanelWidth, setAdvancedPanelWidth] = useState(STATEMENT_ADVANCED_PANEL_DEFAULT_WIDTH)
  const [settings, setSettings] = useState<CfoStatementFeatureSettings>(DEFAULT_STATEMENT_SETTINGS)
  const [localHiddenRowIds, setLocalHiddenRowIds] = useState<Set<string>>(() => new Set())
  const [mergeGroups, setMergeGroups] = useState<CfoStatementMergeGroup[]>([])
  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(() => new Set())
  const [cellSelection, setCellSelection] = useState<CfoStatementCellSelection | null>(null)
  const [contextMenu, setContextMenu] = useState<CfoStatementContextMenuState>(null)
  const [rowEditor, setRowEditor] = useState<CfoStatementRowEditorState>(null)
  const [showHiddenLayoutRows, setShowHiddenLayoutRows] = useState(false)
  const [undoStack, setUndoStack] = useState<CfoStatementUndoEntry[]>([])
  const [redoStack, setRedoStack] = useState<CfoStatementUndoEntry[]>([])
  const [actionStatus, setActionStatus] = useState('')
  const [internalRollupDepth, setInternalRollupDepth] = useState(() => clampStatementRollupDepth(defaultRollupDepth))
  const [dragSourceNodeId, setDragSourceNodeId] = useState('')
  const [focusPulseNodeId, setFocusPulseNodeId] = useState<string | null>(null)
  const dtableWrapRef = useRef<HTMLDivElement | null>(null)
  const rollupDepth = controlledRollupDepth == null ? internalRollupDepth : clampStatementRollupDepth(controlledRollupDepth)
  const rollupControlActive = fullscreen || controlledRollupDepth != null
  const effectiveHideBlankRows = hideBlankRows && settings.hideBlankRows
  const selectingCellsRef = useRef(false)
  const cellSelectionRef = useRef<CfoStatementCellSelection | null>(null)
  const rowSelectionAnchorIdRef = useRef<string | null>(null)
  const fullscreenRollupInitRef = useRef('')
  const rollupUserEditedRef = useRef(false)
  const advancedPanelResizeRef = useRef<{ startX: number; startWidth: number } | null>(null)
  const baseRows = useMemo(() => {
    if (!statement) return []
    return statement.rows.slice(0, maxRows)
  }, [maxRows, statement])
  const blankRowFilterColumns = useMemo(
    () => (statement ? buildColumns({ ...statement, rows: baseRows }, endPeriod, comparisonMode, maxPeriods, periodHeaderFormatter, periodWindow) : []),
    [baseRows, comparisonMode, endPeriod, maxPeriods, periodHeaderFormatter, periodWindow, statement],
  )
  const layoutNodesById = useMemo(
    () => new Map((layoutEdit?.nodes || layoutNodes || []).map((node) => [node.id, node])),
    [layoutEdit?.nodes, layoutNodes],
  )
  const sessionLayoutChanges = useMemo(
    () => undoStack.map(undoEntryToLayoutChangeEntry).filter((entry): entry is CfoStatementAppliedLayoutChangeEntry => Boolean(entry)),
    [undoStack],
  )
  const persistedLayoutChanges = useMemo(
    () => deriveAppliedLayoutChangesFromNodes(layoutEdit?.nodes || []),
    [layoutEdit?.nodes],
  )
  const sessionLayoutChangeNodeIds = useMemo(
    () => layoutChangeEntryNodeIdSet(sessionLayoutChanges),
    [sessionLayoutChanges],
  )
  const layoutChangeCards = useMemo(
    () => [
      ...sessionLayoutChanges,
      ...persistedLayoutChanges.filter((entry) => !layoutChangeTouchesAnyNode(entry, sessionLayoutChangeNodeIds)),
    ].reverse(),
    [persistedLayoutChanges, sessionLayoutChangeNodeIds, sessionLayoutChanges],
  )
  const layoutEditable = Boolean(layoutEdit?.editable && !layoutEdit?.busy)
  const hiddenLayoutNodeCount = useMemo(
    () => (layoutEdit?.nodes || []).filter((node) => node.include_in_reports === false).length,
    [layoutEdit?.nodes],
  )

  const filteredBaseRows = useMemo(() => {
    const groupRowIds = new Set(mergeGroups.flatMap((group) => group.rowIds))
    const sourceRowsById = new Map(baseRows.map((row) => [row.node_id, row]))
    const mergedRowsByFirstIndex = new Map<number, QuickBooksFpaStatementRow[]>()
    for (const group of mergeGroups) {
      const sourceRows = group.rowIds.map((rowId) => sourceRowsById.get(rowId)).filter((row): row is QuickBooksFpaStatementRow => Boolean(row))
      if (!sourceRows.length) continue
      const firstIndex = Math.min(...sourceRows.map((row) => baseRows.findIndex((candidate) => candidate.node_id === row.node_id)).filter((index) => index >= 0))
      if (!Number.isFinite(firstIndex)) continue
      const values = addStatementValues(...sourceRows.map((row) => statementValues(row, statement?.periods.length || row.values.length)))
      const level = Math.min(...sourceRows.map((row) => Number(row.level || 0)))
      const synthetic = profitAndLossRollupRow(sourceRows[0], {
        nodeId: `fg-cfo-local-${group.mode}-${group.id}`,
        label: group.label,
        values,
        rowType: 'group',
        level,
      })
      const existing = mergedRowsByFirstIndex.get(firstIndex) || []
      existing.push(synthetic)
      mergedRowsByFirstIndex.set(firstIndex, existing)
    }
    const next: QuickBooksFpaStatementRow[] = []
    baseRows.forEach((row, index) => {
      const mergedAtIndex = mergedRowsByFirstIndex.get(index)
      if (mergedAtIndex?.length) next.push(...mergedAtIndex)
      if (localHiddenRowIds.has(row.node_id) || groupRowIds.has(row.node_id)) return
      const sourceNode = layoutNodesById.get(statementSourceNodeId(row.node_id))
      if (sourceNode && sourceNode.include_in_reports === false && !showHiddenLayoutRows) return
      if (!settings.showDetailRows && isDetailStatementRow(row)) return
      if (effectiveHideBlankRows && !isCanonicalProfitAndLossControl(row)) {
        const hasDisplayedActivity = blankRowFilterColumns.length
          ? rowHasDisplayedActivity(baseRows, index, blankRowFilterColumns)
          : rowHasStatementActivity(baseRows, index)
        if (!hasDisplayedActivity) return
      }
      next.push(row)
    })
    return next
  }, [baseRows, blankRowFilterColumns, effectiveHideBlankRows, layoutNodesById, localHiddenRowIds, mergeGroups, settings.showDetailRows, showHiddenLayoutRows, statement?.periods.length])

  const baseExpandableRowIds = useMemo(() => {
    const rowIds = new Set(filteredBaseRows.map((row) => row.node_id))
    return new Set([...statementRowParents(filteredBaseRows).values()].filter((id): id is string => Boolean(id && rowIds.has(id))))
  }, [filteredBaseRows])

  const [collapsedRows, setCollapsedRows] = useState<Set<string>>(() => (defaultCollapsed ? new Set(baseExpandableRowIds) : new Set()))
  const collapsedInitRef = useRef('')
  const expandableSignature = useMemo(() => [...baseExpandableRowIds].sort().join('|'), [baseExpandableRowIds])
  const statementPeriodSignature = useMemo(() => statement?.periods.join('|') || '', [statement?.periods])

  useLayoutEffect(() => {
    if (fullscreen && defaultCollapsed) return
    setCollapsedRows((previous) => {
      if (defaultCollapsed && expandableSignature && collapsedInitRef.current !== expandableSignature) {
        collapsedInitRef.current = expandableSignature
        const next = new Set(baseExpandableRowIds)
        if (statement?.statement_type === 'profit_and_loss') addProfitAndLossRollupParentIds(next)
        return next
      }
      const retainedIds = new Set(baseExpandableRowIds)
      if (statement?.statement_type === 'profit_and_loss') addProfitAndLossRollupParentIds(retainedIds)
      const next = new Set([...previous].filter((id) => retainedIds.has(id)))
      return next.size === previous.size ? previous : next
    })
  }, [baseExpandableRowIds, defaultCollapsed, expandableSignature, fullscreen, statement?.statement_type])

  const statementFullyRolledUp = useMemo(
    () => Boolean(settings.cleanCollapsedProfitAndLoss && baseExpandableRowIds.size && [...baseExpandableRowIds].every((id) => collapsedRows.has(id))),
    [baseExpandableRowIds, collapsedRows, settings.cleanCollapsedProfitAndLoss],
  )
  const profitAndLossFullyRolledUp = Boolean(statementFullyRolledUp && statement?.statement_type === 'profit_and_loss')
  const profitAndLossRollupActive = Boolean(
    settings.cleanCollapsedProfitAndLoss &&
    statement?.statement_type === 'profit_and_loss' &&
    (profitAndLossFullyRolledUp || (rollupControlActive && defaultCollapsed)),
  )
  const cleanStatementRollupActive = Boolean(settings.cleanCollapsedProfitAndLoss && (statementFullyRolledUp || (rollupControlActive && defaultCollapsed)))

  const rows = useMemo(() => {
    if (profitAndLossRollupActive) return buildProfitAndLossCollapsedRollupRows(filteredBaseRows, statement?.periods.length || 0)
    return filteredBaseRows
  }, [filteredBaseRows, profitAndLossRollupActive, statement?.periods.length])

  const rowParents = useMemo(() => statementRowParents(rows), [rows])
  const expandableRowIds = useMemo(() => {
    const rowIds = new Set(rows.map((row) => row.node_id))
    return new Set([...rowParents.values()].filter((id): id is string => Boolean(id && rowIds.has(id))))
  }, [rows, rowParents])
  const rollupExpandableSignature = useMemo(() => [...expandableRowIds].sort().join('|'), [expandableRowIds])
  const visibleRowFilterColumns = useMemo(
    () => (statement ? buildColumns({ ...statement, rows }, endPeriod, comparisonMode, maxPeriods, periodHeaderFormatter, periodWindow) : []),
    [comparisonMode, endPeriod, maxPeriods, periodHeaderFormatter, periodWindow, rows, statement],
  )

  const buildCollapsedRowsForRollupDepth = useCallback((depth: number) => {
    const boundedDepth = Math.max(0, Math.min(STATEMENT_ROLLUP_MAX_DEPTH, depth))
    const next = new Set<string>()
    if (profitAndLossRollupActive) {
      baseExpandableRowIds.forEach((id) => next.add(id))
    }
    rows.forEach((row) => {
      if (expandableRowIds.has(row.node_id) && Number(row.level || 0) >= boundedDepth) {
        next.add(row.node_id)
      }
    })
    return next
  }, [baseExpandableRowIds, expandableRowIds, profitAndLossRollupActive, rows])

  const applyRollupDepth = (depth: number) => {
    const boundedDepth = clampStatementRollupDepth(depth)
    rollupUserEditedRef.current = true
    if (controlledRollupDepth == null) setInternalRollupDepth(boundedDepth)
    onRollupDepthChange?.(boundedDepth)
    setCollapsedRows((previous) => {
      const next = buildCollapsedRowsForRollupDepth(boundedDepth)
      return stringSetsEqual(previous, next) ? previous : next
    })
  }

  const visibleRows = useMemo(() => {
    const visible: QuickBooksFpaStatementRow[] = []
    rows.forEach((row, rowIndex) => {
      const level = Number(row.level || 0)
      if (statementRowIsCollapsed(row, rowParents, collapsedRows)) return
      if (profitAndLossRollupActive && isProfitAndLossBoundaryTotalRow(row)) return
      if (profitAndLossRollupActive && isBlankDuplicateProfitAndLossRollupRow(row, rows)) return
      if (cleanStatementRollupActive && !profitAndLossRollupActive && isExplicitStatementTotalRow(row)) return
      if (profitAndLossRollupActive && !fullscreen && !row.parent_node_id && level > rollupDepth) return
      if (effectiveHideBlankRows && visibleRowFilterColumns.length) {
        if (!rowHasDisplayedActivity(rows, rowIndex, visibleRowFilterColumns)) return
      }
      visible.push(row)
    })
    return visible
  }, [cleanStatementRollupActive, collapsedRows, effectiveHideBlankRows, fullscreen, profitAndLossRollupActive, rollupDepth, rows, rowParents, visibleRowFilterColumns])

  // Deep-link: when a driver/flag asks to focus a row, expand its collapsed ancestors, scroll it into
  // view, and briefly highlight it. Keyed on focusNonce so the same node can be re-focused on re-click.
  useEffect(() => {
    if (!focusNonce || !focusNodeId) return
    // The P&L rollup view re-keys account rows as `<parent>:source:<originalId>`, so a deep-link target
    // (a bare layout id) must be matched against either the rendered id or its source id.
    const targetRow =
      rows.find((r) => r.node_id === focusNodeId) ||
      rows.find((r) => statementSourceNodeId(r.node_id) === focusNodeId)
    const targetNodeId = targetRow?.node_id || focusNodeId
    const parentById = new Map(rows.map((r) => [r.node_id, r.parent_node_id || null] as const))
    const ancestors: string[] = []
    let cur = parentById.get(targetNodeId) || null
    let guard = 0
    while (cur && guard < 64) {
      ancestors.push(cur)
      cur = parentById.get(cur) || null
      guard += 1
    }
    if (ancestors.length) {
      setCollapsedRows((previous) => {
        let changed = false
        const next = new Set(previous)
        ancestors.forEach((a) => {
          if (next.delete(a)) changed = true
        })
        return changed ? next : previous
      })
    }
    setFocusPulseNodeId(targetNodeId)
    const selectorId = targetNodeId.replace(/["\\]/g, '\\$&')
    const raf = window.requestAnimationFrame(() => {
      const el = dtableWrapRef.current?.querySelector<HTMLElement>(
        `tr[data-node-id="${selectorId}"]`,
      )
      el?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    })
    const clear = window.setTimeout(() => setFocusPulseNodeId(null), 1900)
    return () => {
      window.cancelAnimationFrame(raf)
      window.clearTimeout(clear)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusNonce])

  const columnStatement = useMemo(
    () => (statement ? { ...statement, rows } : null),
    [rows, statement],
  )
  const columns = useMemo(
    () => (columnStatement ? buildColumns(columnStatement, endPeriod, comparisonMode, maxPeriods, periodHeaderFormatter, periodWindow) : []),
    [columnStatement, comparisonMode, endPeriod, maxPeriods, periodHeaderFormatter, periodWindow],
  )
  const currencyScale = useMemo(
    () => statementCurrencyScale(visibleRows, columns),
    [columns, visibleRows],
  )

  const selectedRows = useMemo(
    () => visibleRows.filter((row) => selectedRowIds.has(row.node_id)),
    [selectedRowIds, visibleRows],
  )
  const selectedLayoutNodes = useMemo(
    () => selectedRows
      .map((row) => layoutNodesById.get(statementSourceNodeId(row.node_id)))
      .filter((node): node is QuickBooksFpaLayoutNode => Boolean(node)),
    [layoutNodesById, selectedRows],
  )

  const selectionBounds = useMemo(() => {
    if (!cellSelection) return null
    return {
      minRowIndex: Math.min(cellSelection.anchor.rowIndex, cellSelection.focus.rowIndex),
      maxRowIndex: Math.max(cellSelection.anchor.rowIndex, cellSelection.focus.rowIndex),
      minColumnIndex: Math.min(cellSelection.anchor.columnIndex, cellSelection.focus.columnIndex),
      maxColumnIndex: Math.max(cellSelection.anchor.columnIndex, cellSelection.focus.columnIndex),
    }
  }, [cellSelection])
  const selectedCellLayoutNode = useMemo(() => {
    if (!cellSelection) return null
    const candidateRows: QuickBooksFpaStatementRow[] = []
    const seenRowIds = new Set<string>()
    const addRow = (rowIndex: number) => {
      const row = visibleRows[rowIndex]
      if (!row || seenRowIds.has(row.node_id)) return
      seenRowIds.add(row.node_id)
      candidateRows.push(row)
    }
    addRow(cellSelection.focus.rowIndex)
    addRow(cellSelection.anchor.rowIndex)
    if (selectionBounds) {
      for (let rowIndex = selectionBounds.minRowIndex; rowIndex <= selectionBounds.maxRowIndex; rowIndex += 1) {
        addRow(rowIndex)
      }
    }
    for (const row of candidateRows) {
      const node = layoutNodesById.get(statementSourceNodeId(row.node_id))
      if (node) return node
    }
    return null
  }, [cellSelection, layoutNodesById, selectionBounds, visibleRows])
  const journalEntryTargetNode = selectedLayoutNodes[0] || selectedCellLayoutNode

  const selectedCellSummary = useMemo(() => {
    if (!selectionBounds) return null
    let count = 0
    let amountCount = 0
    let sum = 0
    for (let rowIndex = selectionBounds.minRowIndex; rowIndex <= selectionBounds.maxRowIndex; rowIndex += 1) {
      const row = visibleRows[rowIndex]
      if (!row) continue
      for (let columnIndex = selectionBounds.minColumnIndex; columnIndex <= selectionBounds.maxColumnIndex; columnIndex += 1) {
        const column = columns[columnIndex]
        if (!column) continue
        count += 1
        if (column.kind === 'value' || column.kind === 'delta') {
          const value = column.read(row)
          if (value != null && Number.isFinite(value)) {
            amountCount += 1
            sum += value
          }
        }
      }
    }
    return { count, amountCount, sum }
  }, [columns, selectionBounds, visibleRows])

  const selectedCellClipboardText = useMemo(() => {
    if (!selectionBounds) return ''
    const lines: string[] = []
    for (let rowIndex = selectionBounds.minRowIndex; rowIndex <= selectionBounds.maxRowIndex; rowIndex += 1) {
      const row = visibleRows[rowIndex]
      if (!row) continue
      const values: string[] = []
      for (let columnIndex = selectionBounds.minColumnIndex; columnIndex <= selectionBounds.maxColumnIndex; columnIndex += 1) {
        const column = columns[columnIndex]
        values.push(column ? formatStatementClipboardValue(column, row, currencyScale, percentOfSales) : '')
      }
      lines.push(values.join('\t'))
    }
    return lines.join('\n')
  }, [columns, currencyScale, percentOfSales, selectionBounds, visibleRows])

  const copySelectedCells = useCallback(async () => {
    if (!selectedCellClipboardText || !selectedCellSummary) return
    await copyTextToClipboard(selectedCellClipboardText)
    setActionStatus(`Copied ${selectedCellSummary.count} cell${selectedCellSummary.count === 1 ? '' : 's'}.`)
  }, [selectedCellClipboardText, selectedCellSummary])

  useEffect(() => {
    const stopSelecting = () => {
      selectingCellsRef.current = false
    }
    window.addEventListener('mouseup', stopSelecting)
    return () => window.removeEventListener('mouseup', stopSelecting)
  }, [])

  useEffect(() => {
    const handleCopy = (event: ClipboardEvent) => {
      if (!fullscreen || !settings.enableCellSelection || !selectedCellClipboardText || !selectedCellSummary) return
      event.clipboardData?.setData('text/plain', selectedCellClipboardText)
      event.preventDefault()
      setActionStatus(`Copied ${selectedCellSummary.count} cell${selectedCellSummary.count === 1 ? '' : 's'}.`)
    }
    document.addEventListener('copy', handleCopy)
    return () => document.removeEventListener('copy', handleCopy)
  }, [fullscreen, selectedCellClipboardText, selectedCellSummary, settings.enableCellSelection])

  useEffect(() => {
    const resizePanel = (event: MouseEvent) => {
      const resizeState = advancedPanelResizeRef.current
      if (!resizeState) return
      const nextWidth = resizeState.startWidth + resizeState.startX - event.clientX
      setAdvancedPanelWidth(Math.max(
        STATEMENT_ADVANCED_PANEL_MIN_WIDTH,
        Math.min(STATEMENT_ADVANCED_PANEL_MAX_WIDTH, nextWidth),
      ))
    }
    const stopResizing = () => {
      advancedPanelResizeRef.current = null
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
    window.addEventListener('mousemove', resizePanel)
    window.addEventListener('mouseup', stopResizing)
    return () => {
      window.removeEventListener('mousemove', resizePanel)
      window.removeEventListener('mouseup', stopResizing)
      stopResizing()
    }
  }, [])

  useEffect(() => {
    rowSelectionAnchorIdRef.current = null
    setSelectedRowIds(new Set())
    setCellSelection(null)
    cellSelectionRef.current = null
    setContextMenu(null)
  }, [statement?.statement_type, statementPeriodSignature])

  useLayoutEffect(() => {
    if (!rollupControlActive) {
      fullscreenRollupInitRef.current = ''
      rollupUserEditedRef.current = false
      return
    }
    if (!defaultCollapsed) return
    if (!settings.cleanCollapsedProfitAndLoss) return
    if (statement?.statement_type === 'profit_and_loss' && !rollupExpandableSignature) return
    const initSignature = `${statement?.statement_type || 'statement'}::${expandableSignature}::${rollupExpandableSignature}::${rollupDepth}`
    if (fullscreenRollupInitRef.current !== initSignature) {
      fullscreenRollupInitRef.current = initSignature
      rollupUserEditedRef.current = false
    }
    if (rollupUserEditedRef.current) return
    setCollapsedRows((previous) => {
      const next = buildCollapsedRowsForRollupDepth(rollupDepth)
      return stringSetsEqual(previous, next) ? previous : next
    })
  }, [buildCollapsedRowsForRollupDepth, defaultCollapsed, expandableSignature, rollupControlActive, rollupDepth, rollupExpandableSignature, settings.cleanCollapsedProfitAndLoss, statement?.statement_type])

  if (!statement && loading) {
    return <CfoTableSkeleton columns={Math.min(Math.max(maxPeriods + 1, 4), 8)} rows={10} label={loadingLabel} />
  }

  // Not loading — there is genuinely no statement. The old copy claimed a load was
  // in flight ("loading from the workbook binding"), so callers that withhold a
  // statement on purpose left the reader waiting on a spinner that never resolved.
  if (!statement) {
    return <div className="cfo-shell-notice">{emptyLabel}</div>
  }

  const updateSetting = (key: keyof CfoStatementFeatureSettings, value: boolean) => {
    setSettings((current) => ({ ...current, [key]: value }))
  }

  const clearCellSelection = () => {
    cellSelectionRef.current = null
    setCellSelection(null)
  }

  const startAdvancedPanelResize = (event: ReactMouseEvent) => {
    if (!advancedOpen) return
    event.preventDefault()
    advancedPanelResizeRef.current = {
      startX: event.clientX,
      startWidth: advancedPanelWidth,
    }
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
  }

  const resizeAdvancedPanelBy = (delta: number) => {
    setAdvancedPanelWidth((current) => Math.max(
      STATEMENT_ADVANCED_PANEL_MIN_WIDTH,
      Math.min(STATEMENT_ADVANCED_PANEL_MAX_WIDTH, current + delta),
    ))
  }

  const selectRow = (row: QuickBooksFpaStatementRow, event: ReactMouseEvent) => {
    if (!fullscreen) return
    event.preventDefault()
    setContextMenu(null)
    const visibleIds = visibleRows.map((candidate) => candidate.node_id)
    const targetIndex = visibleIds.indexOf(row.node_id)
    const isToggle = event.metaKey || event.ctrlKey
    if (!event.shiftKey) {
      rowSelectionAnchorIdRef.current = row.node_id
    } else if (!rowSelectionAnchorIdRef.current || !visibleIds.includes(rowSelectionAnchorIdRef.current)) {
      rowSelectionAnchorIdRef.current = row.node_id
    }
    const anchorId = rowSelectionAnchorIdRef.current
    setSelectedRowIds((previous) => {
      if (event.shiftKey && anchorId) {
        const anchorIndex = visibleIds.indexOf(anchorId)
        if (anchorIndex >= 0 && targetIndex >= 0) {
          const from = Math.min(anchorIndex, targetIndex)
          const to = Math.max(anchorIndex, targetIndex)
          const rangeSelection = visibleIds.slice(from, to + 1)
          return isToggle ? new Set([...previous, ...rangeSelection]) : new Set(rangeSelection)
        }
      }
      if (isToggle) {
        const next = new Set(previous)
        if (next.has(row.node_id)) next.delete(row.node_id)
        else next.add(row.node_id)
        return next
      }
      return new Set([row.node_id])
    })
  }

  const startCellSelection = (rowIndex: number, columnIndex: number, event: ReactMouseEvent) => {
    if (!fullscreen || !settings.enableCellSelection) return
    if (event.button !== 0) return
    event.preventDefault()
    const focus = { rowIndex, columnIndex }
    const current = cellSelectionRef.current
    const next = event.shiftKey && current ? { anchor: current.anchor, focus } : { anchor: focus, focus }
    selectingCellsRef.current = true
    cellSelectionRef.current = next
    setCellSelection(next)
    setContextMenu(null)
  }

  const extendCellSelection = (rowIndex: number, columnIndex: number) => {
    if (!selectingCellsRef.current || !fullscreen || !settings.enableCellSelection) return
    const current = cellSelectionRef.current
    const focus = { rowIndex, columnIndex }
    const next = current ? { anchor: current.anchor, focus } : { anchor: focus, focus }
    cellSelectionRef.current = next
    setCellSelection(next)
  }

  const finishCellSelection = (rowIndex: number, columnIndex: number, event: ReactMouseEvent) => {
    if (!fullscreen || !settings.enableCellSelection) return
    if (event.button !== 0 || !selectingCellsRef.current) return
    const current = cellSelectionRef.current
    const focus = { rowIndex, columnIndex }
    const next = current ? { anchor: current.anchor, focus } : { anchor: focus, focus }
    cellSelectionRef.current = next
    setCellSelection(next)
    selectingCellsRef.current = false
  }

  const isCellSelected = (rowIndex: number, columnIndex: number) =>
    Boolean(selectionBounds && rowIndex >= selectionBounds.minRowIndex && rowIndex <= selectionBounds.maxRowIndex && columnIndex >= selectionBounds.minColumnIndex && columnIndex <= selectionBounds.maxColumnIndex)

  const buildGlDetailCell = (
    row: QuickBooksFpaStatementRow,
    column: CfoStatementColumn | null | undefined,
  ): CfoStatementGlDetailCell => ({
    nodeId: statementSourceNodeId(row.node_id),
    rowLabel: displayStatementRowLabel(row, settings.showAccountCodes),
    statementType: row.statement_type || statement?.statement_type || null,
    accountId: row.account_id || null,
    accountNumber: row.account_number || null,
    accountName: row.label || null,
    columnKey: column?.key || 'row',
    columnLabel: column?.headerLabel || 'Row',
    period: column?.period,
    value: column ? column.read(row) : null,
  })

  const selectedGlDetailCells = () => {
    if (!selectionBounds) return []
    const cells: CfoStatementGlDetailCell[] = []
    const seen = new Set<string>()
    for (let rowIndex = selectionBounds.minRowIndex; rowIndex <= selectionBounds.maxRowIndex; rowIndex += 1) {
      const row = visibleRows[rowIndex]
      if (!row) continue
      for (let columnIndex = selectionBounds.minColumnIndex; columnIndex <= selectionBounds.maxColumnIndex; columnIndex += 1) {
        const column = columns[columnIndex]
        if (!column || column.kind === 'pct') continue
        const cell = buildGlDetailCell(row, column)
        const key = [cell.nodeId, cell.columnKey, cell.period || '', cell.accountId || '', cell.accountNumber || '', cell.accountName || ''].join('|')
        if (seen.has(key)) continue
        seen.add(key)
        cells.push(cell)
      }
    }
    return cells
  }

  const runLayoutPatch = async (label: string, patch: CfoStatementLayoutPatch, options?: { trackUndo?: boolean }) => {
    if (!layoutEdit || !layoutEditable) return false
    if (!patch.nodes?.length && !patch.delete_node_ids?.length) return false
    const before = invertLayoutPatch(layoutNodesById, patch)
    try {
      await layoutEdit.onPatch(patch)
      if (options?.trackUndo !== false && (before.nodes?.length || before.delete_node_ids?.length)) {
        setUndoStack((current) => [...current.slice(-19), { id: createClientId(), label, before, after: patch }])
        setRedoStack([])
      }
      setActionStatus(label)
      return true
    } catch (error: any) {
      setActionStatus(error?.message || 'Layout change failed.')
      return false
    }
  }

  const createMergeGroup = async (mode: CfoStatementMergeGroup['mode']) => {
    if (!fullscreen || selectedRows.length < 2) return
    if (layoutEdit && layoutEditable && selectedLayoutNodes.length >= 2) {
      const sourceNodes = [...selectedLayoutNodes].sort(compareQuickBooksFpaLayoutNodes)
      const commonParentId = sourceNodes.every((node) => (node.parent_node_id || '') === (sourceNodes[0].parent_node_id || ''))
        ? sourceNodes[0].parent_node_id || null
        : null
      const groupId = createClientId()
      const groupSortOrder = Math.min(...sourceNodes.map((node) => normalizeQuickBooksFpaSortOrder(node.sort_order)))
      const groupLabel = mode === 'merge' ? 'Merged group' : 'New group'
      const metadataKey = mode === 'merge' ? 'cfoCoreUserMergedGroup' : 'cfoCoreUserRollupGroup'
      const patches: NonNullable<CfoStatementLayoutPatch['nodes']> = [{
        id: groupId,
        node_type: 'group',
        statement_type: sourceNodes[0].statement_type || statement?.statement_type || 'profit_and_loss',
        parent_node_id: commonParentId || undefined,
        clear_parent: !commonParentId,
        cash_flow_section: sourceNodes[0].cash_flow_section || undefined,
        clear_cash_flow_section: !sourceNodes[0].cash_flow_section,
        display_name: groupLabel,
        include_in_reports: true,
        sort_order: groupSortOrder - 1,
        metadata: { [metadataKey]: true, createdFromNodeIds: sourceNodes.map((node) => node.id) },
      }]
      sourceNodes.forEach((node, index) => {
        patches.push({
          id: node.id,
          parent_node_id: groupId,
          sort_order: (index + 1) * 10,
          metadata: mode === 'merge'
            ? {
              ...(node.metadata || {}),
              cfoCoreMerge: {
                mergedIntoNodeId: groupId,
                previousParentNodeId: node.parent_node_id || null,
                previousSortOrder: node.sort_order,
                mergedAt: new Date().toISOString(),
              },
            }
            : {
              ...(node.metadata || {}),
              cfoCoreRollup: {
                rolledIntoNodeId: groupId,
                previousParentNodeId: node.parent_node_id || null,
                previousSortOrder: node.sort_order,
                rolledAt: new Date().toISOString(),
              },
            },
        })
      })
      if (await runLayoutPatch(mode === 'merge' ? `Merged ${sourceNodes.length} rows into one group.` : `Rolled ${sourceNodes.length} rows under a drillable group.`, { nodes: patches })) {
        rowSelectionAnchorIdRef.current = groupId
        setSelectedRowIds(new Set([groupId]))
      }
      return
    }
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    const label = mode === 'merge'
      ? `Merged ${selectedRows.length} rows`
      : `Rolled up ${selectedRows.length} rows`
    setMergeGroups((current) => [...current, { id, label, rowIds: selectedRows.map((row) => statementSourceNodeId(row.node_id)), mode }])
    rowSelectionAnchorIdRef.current = null
    setSelectedRowIds(new Set())
    setActionStatus(`${label} in the current statement view.`)
  }

  const unmergeSelectedRows = async () => {
    const rows = selectedLayoutNodes.filter((node) => node.metadata?.cfoCoreMerge)
    const selectedGroups = selectedLayoutNodes.filter((node) => node.metadata?.cfoCoreUserMergedGroup)
    const groupChildRows = selectedGroups.flatMap((group) => (
      (layoutEdit?.nodes || []).filter((node) => String(node.metadata?.cfoCoreMerge?.mergedIntoNodeId || '') === group.id)
    ))
    const allRows = [...rows, ...groupChildRows]
    if (!allRows.length) return
    const patches = allRows.map((node) => {
      const merge = node.metadata?.cfoCoreMerge || {}
      return {
        id: node.id,
        parent_node_id: merge.previousParentNodeId || undefined,
        clear_parent: !merge.previousParentNodeId,
        sort_order: normalizeQuickBooksFpaSortOrder(merge.previousSortOrder, normalizeQuickBooksFpaSortOrder(node.sort_order)),
        metadata: stripMergeMetadata(node.metadata),
      }
    })
    const groupDeletes = selectedGroups.map((group) => group.id)
    await runLayoutPatch(`Unmerged ${allRows.length} row${allRows.length === 1 ? '' : 's'}.`, {
      nodes: patches,
      delete_node_ids: groupDeletes.length ? groupDeletes : undefined,
    })
  }

  const hideSelectedRows = async () => {
    if (!fullscreen || !selectedRows.length) return
    if (layoutEdit && layoutEditable && selectedLayoutNodes.length) {
      await runLayoutPatch(`Hidden ${selectedLayoutNodes.length} row${selectedLayoutNodes.length === 1 ? '' : 's'} from reports.`, {
        nodes: selectedLayoutNodes.map((node) => ({ id: node.id, include_in_reports: false })),
      })
      rowSelectionAnchorIdRef.current = null
      setSelectedRowIds(new Set())
      return
    }
    setLocalHiddenRowIds((current) => new Set([...current, ...selectedRows.map((row) => statementSourceNodeId(row.node_id))]))
    rowSelectionAnchorIdRef.current = null
    setSelectedRowIds(new Set())
    setActionStatus(`Hidden ${selectedRows.length} row${selectedRows.length === 1 ? '' : 's'} in this view.`)
  }

  const showSelectedRows = async () => {
    if (!selectedLayoutNodes.length) return
    await runLayoutPatch(`Shown ${selectedLayoutNodes.length} row${selectedLayoutNodes.length === 1 ? '' : 's'} in reports.`, {
      nodes: selectedLayoutNodes.map((node) => ({ id: node.id, include_in_reports: true })),
    })
  }

  const showAllLocalRows = async () => {
    if (layoutEdit && layoutEditable && hiddenLayoutNodeCount) {
      await runLayoutPatch(`Shown ${hiddenLayoutNodeCount} hidden row${hiddenLayoutNodeCount === 1 ? '' : 's'} in reports.`, {
        nodes: (layoutEdit.nodes || [])
          .filter((node) => node.include_in_reports === false)
          .map((node) => ({ id: node.id, include_in_reports: true })),
      })
    }
    setLocalHiddenRowIds(new Set())
    setMergeGroups([])
    setActionStatus('Restored hidden and locally merged rows in this view.')
  }

  const moveLayoutNode = async (node: QuickBooksFpaLayoutNode | null | undefined, direction: -1 | 1) => {
    if (!node) return
    await runLayoutPatch(direction < 0 ? `Moved ${node.display_name} up.` : `Moved ${node.display_name} down.`, {
      nodes: [{ id: node.id, sort_order: normalizeQuickBooksFpaSortOrder(node.sort_order) + direction * 11 }],
    })
  }

  const moveSelectedNode = async (direction: -1 | 1) => {
    await moveLayoutNode(firstEditableNode(selectedRows, layoutNodesById), direction)
  }

  const indentLayoutNode = async (node: QuickBooksFpaLayoutNode | null | undefined) => {
    if (!node) return
    const siblings = (layoutEdit?.nodes || [])
      .filter((candidate) => (candidate.parent_node_id || '') === (node.parent_node_id || '') && candidate.id !== node.id)
      .sort(compareQuickBooksFpaLayoutNodes)
    const previous = [...siblings].reverse().find((candidate) => compareQuickBooksFpaLayoutNodes(candidate, node) <= 0)
    if (!previous || previous.node_type === 'account') return
    await runLayoutPatch(`Indented ${node.display_name}.`, { nodes: [{ id: node.id, parent_node_id: previous.id }] })
  }

  const indentSelectedNode = async () => {
    await indentLayoutNode(firstEditableNode(selectedRows, layoutNodesById))
  }

  const outdentLayoutNode = async (node: QuickBooksFpaLayoutNode | null | undefined) => {
    if (!node?.parent_node_id) return
    const parent = layoutNodesById.get(node.parent_node_id)
    await runLayoutPatch(`Outdented ${node.display_name}.`, {
      nodes: [{ id: node.id, parent_node_id: parent?.parent_node_id || undefined, clear_parent: !parent?.parent_node_id }],
    })
  }

  const outdentSelectedNode = async () => {
    await outdentLayoutNode(firstEditableNode(selectedRows, layoutNodesById))
  }

  const insertRowNear = async (row?: QuickBooksFpaStatementRow | null) => {
    const target = row ? layoutNodesById.get(statementSourceNodeId(row.node_id)) : firstEditableNode(selectedRows, layoutNodesById)
    if (!target || !layoutEdit) return
    const patch = buildQuickBooksFpaInsertRowPatch({
      id: createClientId(),
      targetNode: target,
      nodes: layoutEdit.nodes,
      statementType: target.statement_type || statement?.statement_type,
      nodeType: 'group',
      displayName: 'New row',
    })
    if (!patch) return
    await runLayoutPatch(`Inserted row below ${target.display_name}.`, { nodes: [patch] })
    setRowEditor({
      nodeId: patch.id,
      displayName: patch.display_name || 'New row',
      statementType: patch.statement_type || target.statement_type || statement?.statement_type || 'profit_and_loss',
      includeInReports: patch.include_in_reports !== false,
    })
  }

  const undoLayoutEdit = async () => {
    const entry = undoStack[undoStack.length - 1]
    if (!entry || !layoutEdit || !layoutEditable) return
    await layoutEdit.onPatch(entry.before)
    setUndoStack((current) => current.slice(0, -1))
    setRedoStack((current) => [...current, entry])
    setActionStatus(`Undid ${entry.label}`)
  }

  const redoLayoutEdit = async () => {
    const entry = redoStack[redoStack.length - 1]
    if (!entry || !layoutEdit || !layoutEditable) return
    await layoutEdit.onPatch(entry.after)
    setRedoStack((current) => current.slice(0, -1))
    setUndoStack((current) => [...current, entry])
    setActionStatus(`Redid ${entry.label}`)
  }

  const openLayoutChangeEditor = (entry: CfoStatementAppliedLayoutChangeEntry) => {
    const nodeId = entry.nodeId
    const node = nodeId ? layoutNodesById.get(nodeId) : null
    const afterPatch = entry.after
    const beforePatch = entry.before
    const displayName = String(node?.display_name || afterPatch?.display_name || beforePatch?.display_name || '').trim()
    if (!nodeId || !displayName) return
    rowSelectionAnchorIdRef.current = nodeId
    setSelectedRowIds(new Set([nodeId]))
    setRowEditor({
      nodeId,
      displayName,
      statementType: String(node?.statement_type || afterPatch?.statement_type || beforePatch?.statement_type || statement?.statement_type || 'profit_and_loss'),
      includeInReports: (node?.include_in_reports ?? afterPatch?.include_in_reports ?? beforePatch?.include_in_reports) !== false,
    })
  }

  const deleteLayoutChange = async (entry: CfoStatementAppliedLayoutChangeEntry) => {
    if (!layoutEdit || !layoutEditable) return
    await layoutEdit.onPatch(layoutChangePatchRequest(entry, 'before'))
    if (entry.sourceUndoEntryId) {
      setUndoStack((current) => current.filter((candidate) => candidate.id !== entry.sourceUndoEntryId))
    }
    setRedoStack([])
    const nodeId = entry.nodeId
    if (nodeId) {
      rowSelectionAnchorIdRef.current = nodeId
      setSelectedRowIds(new Set([nodeId]))
    }
    setActionStatus(`Deleted ${layoutChangeRowLabel(entry, layoutNodesById)}`)
  }

  const openRowEditor = (row: QuickBooksFpaStatementRow) => {
    const node = layoutNodesById.get(statementSourceNodeId(row.node_id))
    if (!node) return
    rowSelectionAnchorIdRef.current = row.node_id
    setSelectedRowIds(new Set([row.node_id]))
    setRowEditor({
      nodeId: node.id,
      displayName: node.display_name || row.label || '',
      statementType: node.statement_type || statement?.statement_type || 'profit_and_loss',
      includeInReports: node.include_in_reports !== false,
    })
  }

  const saveRowEditor = async () => {
    if (!rowEditor) return
    const displayName = rowEditor.displayName.trim()
    if (!displayName) return
    await runLayoutPatch(`Renamed row to ${displayName}.`, {
      nodes: [{
        id: rowEditor.nodeId,
        display_name: displayName,
        statement_type: rowEditor.statementType,
        include_in_reports: rowEditor.includeInReports,
      }],
    })
    setRowEditor(null)
  }

  const moveDraggedNode = async (targetRow: QuickBooksFpaStatementRow) => {
    if (!dragSourceNodeId || !layoutEdit) return
    const dragged = layoutNodesById.get(dragSourceNodeId)
    const target = layoutNodesById.get(statementSourceNodeId(targetRow.node_id))
    if (!dragged || !target || dragged.id === target.id) return
    const patch = buildQuickBooksFpaMoveRowPatch({ dragged, target, nodes: layoutEdit.nodes, position: 'below' })
    if (!patch) return
    await runLayoutPatch(`Moved ${dragged.display_name} below ${target.display_name}.`, { nodes: [patch] })
    setDragSourceNodeId('')
  }

  const openContextMenu = (event: ReactMouseEvent, row: QuickBooksFpaStatementRow, column?: CfoStatementColumn | null) => {
    if (!fullscreen || !settings.enableRightClickGlDetail) return
    event.preventDefault()
    setContextMenu({
      x: event.clientX,
      y: event.clientY,
      row,
      column,
      value: column ? column.read(row) : null,
    })
  }

  const openGlDetailFromContext = () => {
    if (!contextMenu) return
    const contextRowIndex = visibleRows.findIndex((row) => row.node_id === contextMenu.row.node_id)
    const contextColumnIndex = contextMenu.column ? columns.findIndex((column) => column.key === contextMenu.column?.key) : -1
    const useCellRange = contextColumnIndex >= 0 && isCellSelected(contextRowIndex, contextColumnIndex)
    const rangeCells = useCellRange ? selectedGlDetailCells() : []
    const contextCell = buildGlDetailCell(contextMenu.row, contextMenu.column)
    const selectedCells = rangeCells.length > 1 ? rangeCells : undefined
    onOpenGlDetail?.({
      ...contextCell,
      ...(selectedCells ? { selectedCells } : {}),
    })
    setActionStatus(
      selectedCells
        ? `GL detail requested for ${selectedCells.length} selected cells.`
        : `GL detail requested for ${contextMenu.row.label}${contextMenu.column ? ` / ${contextMenu.column.headerLabel}` : ''}.`,
    )
    setContextMenu(null)
  }

  const renderCell = (column: CfoStatementColumn, row: QuickBooksFpaStatementRow, rowIndex: number, columnIndex: number) => {
    const value = column.read(row)
    const pctOfSales = column.readPctOfSales?.(row) ?? null
    const decoration = cellDecorator && column.kind === 'value' && column.periodIndex != null
      ? cellDecorator(row, column.periodIndex)
      : null
    const className = [
      (percentOfSales && column.kind !== 'pct' ? pctOfSales : value) == null || Math.abs(Number(percentOfSales && column.kind !== 'pct' ? pctOfSales : value) || 0) < 0.005 ? 'mut' : 'num',
      isCellSelected(rowIndex, columnIndex) ? 'cfo-statement-cell-selected' : '',
      decoration ? `cfo-statement-cell-${decoration.tone}` : '',
    ].filter(Boolean).join(' ')
    const explainable = Boolean(onExplainCell && column.kind === 'value' && column.periodIndex != null)
    const currencyText = column.kind === 'pct' ? formatDeltaPct(value) : formatStatementCurrency(value, currencyScale)
    const percentText = column.kind === 'pct' ? null : formatStatementPercentOfSales(pctOfSales, column.kind === 'delta')
    const content = percentOfSales && column.kind !== 'pct'
      ? percentText
      : showCurrencyAndPercent && column.kind !== 'pct' && percentText !== '—'
        ? (
          <span className="cfo-statement-cell-stack">
            <span>{currencyText}</span>
            <small>{percentText}</small>
          </span>
        )
        : currencyText
    return (
      <td
        key={`${row.node_id}-${column.key}`}
        className={className}
        title={decoration?.title}
        data-cfo-statement-cell-selected={isCellSelected(rowIndex, columnIndex) ? 'true' : 'false'}
        onMouseDown={(event) => startCellSelection(rowIndex, columnIndex, event)}
        onMouseEnter={() => extendCellSelection(rowIndex, columnIndex)}
        onMouseUp={(event) => finishCellSelection(rowIndex, columnIndex, event)}
        onClick={explainable ? () => onExplainCell?.(buildGlDetailCell(row, column)) : undefined}
        onContextMenu={(event) => openContextMenu(event, row, column)}
      >
        {content}
      </td>
    )
  }

  const advancedPanelVisible = Boolean(fullscreen && advancedOpen && settings.showFullscreenEditActions)
  const handleStatementWheel = (event: ReactWheelEvent<HTMLDivElement>) => {
    if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return
    if (event.cancelable) event.preventDefault()
    const scrollTarget = event.currentTarget.closest<HTMLElement>('.cfo-shell-main') || document.scrollingElement
    scrollTarget?.scrollBy({ top: event.deltaY, left: 0, behavior: 'auto' })
  }

  return (
    <div className={[
      'cfo-statement-table',
      fullscreen ? 'fullscreen' : '',
      cleanStatementRollupActive ? 'clean-statement-rollup' : '',
      profitAndLossRollupActive ? 'clean-profit-and-loss-rollup' : '',
    ].filter(Boolean).join(' ')}>
      {fullscreen || toolbarLeading ? (
        <div className="cfo-statement-table-toolbar" aria-label="Statement row controls">
          {toolbarLeading ? <div className="cfo-statement-table-leading">{toolbarLeading}</div> : null}
          {fullscreen ? (
            <div className="cfo-statement-fullscreen-actions">
              {settings.showFullscreenEditActions ? (
                <button
                  type="button"
                  className="btn cfo-statement-advanced-toggle"
                  aria-pressed={advancedOpen}
                  aria-expanded={advancedPanelVisible}
                  onClick={() => setAdvancedOpen((value) => !value)}
                >
                  {advancedPanelVisible ? <PanelRightClose size={15} aria-hidden="true" /> : <PanelRightOpen size={15} aria-hidden="true" />}
                  Advanced
                </button>
              ) : null}
              {settings.cleanCollapsedProfitAndLoss ? (
                <label className="cfo-statement-rollup-depth" title="Set the statement rollup depth">
                  <span>Rollup</span>
                  <input
                    type="range"
                    min={0}
                    max={STATEMENT_ROLLUP_MAX_DEPTH}
                    step={1}
                    value={rollupDepth}
                    aria-label="Statement rollup depth"
                    onChange={(event) => applyRollupDepth(Number(event.target.value))}
                    onInput={(event) => applyRollupDepth(Number(event.currentTarget.value))}
                  />
                  <b>{rollupDepth === STATEMENT_ROLLUP_MAX_DEPTH ? 'All' : rollupDepth}</b>
                </label>
              ) : null}
              <button
                type="button"
                className="btn icon"
                aria-label="Statement settings"
                title="Statement settings"
                onClick={() => setSettingsOpen(true)}
              >
                <Settings size={16} aria-hidden="true" />
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
      {actionStatus && !fullscreen ? <div className="cfo-statement-action-status">{actionStatus}</div> : null}
      {settingsOpen ? (
        <div className="cfo-statement-settings-backdrop" role="presentation" onMouseDown={() => setSettingsOpen(false)}>
          <section
            className="cfo-statement-settings-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Statement settings"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="cfo-statement-settings-head">
              <div>
                <h3>Statement settings</h3>
                <p>Control the behavior of this formatted statement.</p>
              </div>
              <button type="button" className="btn icon" aria-label="Close statement settings" onClick={() => setSettingsOpen(false)}>
                <X size={16} aria-hidden="true" />
              </button>
            </div>
            <div className="cfo-statement-settings-grid">
              {([
                ['hideBlankRows', 'Hide blank rows', 'Remove zero-value detail rows from the view.'],
                ['showDetailRows', 'Show detail rows', 'Keep account-level rows available when sections are expanded.'],
                ['showAccountCodes', 'Show account codes', 'Display account numbers beside row labels.'],
                ['cleanCollapsedProfitAndLoss', 'Clean collapsed rollup', 'When fully collapsed, remove duplicate statement total rows.'],
                ['enableCellSelection', 'Excel-style selection', 'Enable drag selection of cell ranges in full screen.'],
                ['enableRightClickGlDetail', 'Right-click GL detail', 'Show GL detail as a context menu action instead of opening automatically.'],
                ['showFullscreenEditActions', 'Fullscreen edit actions', 'Show merge, roll-up, hide, and show controls in full screen.'],
              ] as Array<[keyof CfoStatementFeatureSettings, string, string]>).map(([key, label, description]) => (
                <label key={key} className="cfo-statement-setting-row">
                  <span>
                    <strong>{label}</strong>
                    <small>{description}</small>
                  </span>
                  <input
                    type="checkbox"
                    checked={settings[key]}
                    onChange={(event) => updateSetting(key, event.target.checked)}
                  />
                </label>
              ))}
            </div>
          </section>
        </div>
      ) : null}
      <div className="cfo-statement-table-workspace" data-advanced-open={advancedPanelVisible ? 'true' : 'false'}>
        <div className="cfo-statement-table-main">
          <div className="dtable-wrap" ref={dtableWrapRef} onWheel={handleStatementWheel}>
            <table className="dtable">
              <thead>
                <tr>
                  <th data-cfo-statement-row-header="true">Account / group</th>
                  {columns.map((column) => (
                    <th key={column.key}>{column.header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row, index) => {
                  const hasChildren = expandableRowIds.has(row.node_id)
                  const expanded = !collapsedRows.has(row.node_id)
                  const className = [
                    rowClass(row),
                    selectedRowIds.has(row.node_id) ? 'selected' : '',
                    row.node_id === focusPulseNodeId ? 'cfo-row-focus-pulse' : '',
                  ].filter(Boolean).join(' ') || undefined
                  const layoutNode = layoutNodesById.get(statementSourceNodeId(row.node_id))
                  return (
                    <tr
                      key={`${row.node_id}-${index}`}
                      data-node-id={row.node_id}
                      className={className}
                      onDragOver={(event) => {
                        if (dragSourceNodeId) event.preventDefault()
                      }}
                      onDrop={(event) => {
                        event.preventDefault()
                        void moveDraggedNode(row)
                      }}
                    >
                      <td
                        data-cfo-statement-row-header="true"
                        onClick={(event) => selectRow(row, event)}
                      >
                        <div
                          className="cfo-statement-row-label"
                          draggable={Boolean(fullscreen && layoutEditable && layoutNode)}
                          style={{ paddingLeft: `${Math.max(0, row.level) * 12}px` }}
                          onDoubleClick={(event) => {
                            event.preventDefault()
                            event.stopPropagation()
                            openRowEditor(row)
                          }}
                          onDragStart={(event) => {
                            if (!layoutNode) return
                            setDragSourceNodeId(layoutNode.id)
                            event.dataTransfer.setData('text/plain', layoutNode.id)
                          }}
                          onDragEnd={() => setDragSourceNodeId('')}
                          onContextMenu={(event) => {
                            openContextMenu(event, row, null)
                          }}
                        >
                          {hasChildren ? (
                            <button
                              type="button"
                              className="cfo-statement-row-toggle"
                              aria-label={`${expanded ? 'Collapse' : 'Expand'} ${row.label}`}
                              aria-expanded={expanded}
                              onClick={(event) => {
                                event.preventDefault()
                                event.stopPropagation()
                                rollupUserEditedRef.current = true
                                setCollapsedRows((previous) => {
                                  const next = new Set(previous)
                                  if (next.has(row.node_id)) next.delete(row.node_id)
                                  else next.add(row.node_id)
                                  return next
                                })
                              }}
                            >
                              <span aria-hidden="true">⌄</span>
                            </button>
                          ) : (
                            <span className="cfo-statement-row-toggle-spacer" aria-hidden="true" />
                          )}
                          <span>
                            {displayStatementRowLabel(row, settings.showAccountCodes)}
                          </span>
                          {onAnalyzeRow ? (
                            <button
                              type="button"
                              className="btn icon cfo-statement-row-analyze"
                              aria-label={`Analyze ${displayStatementRowLabel(row, settings.showAccountCodes)}`}
                              title="Analyze this row"
                              onClick={(event) => {
                                event.preventDefault()
                                event.stopPropagation()
                                onAnalyzeRow(row)
                              }}
                            >
                              <Sparkles size={13} aria-hidden="true" />
                            </button>
                          ) : null}
                          {rowBadge ? rowBadge(row) : null}
                        </div>
                      </td>
                      {columns.map((column, columnIndex) => renderCell(column, row, index, columnIndex))}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          {selectedCellSummary && fullscreen && settings.enableCellSelection ? (
            <div className="cfo-statement-selection-summary">
              <span>{selectedCellSummary.count} cell{selectedCellSummary.count === 1 ? '' : 's'}</span>
              <b>Sum {formatStatementCurrency(selectedCellSummary.sum, currencyScale)}</b>
              <button type="button" className="btn" onClick={() => void copySelectedCells()}>Copy</button>
              <button type="button" className="btn" onClick={clearCellSelection}>Clear</button>
            </div>
          ) : null}
        </div>
        {advancedPanelVisible ? (
          <aside
            className="cfo-statement-advanced-panel"
            aria-label="Advanced statement controls"
            style={{ width: advancedPanelWidth }}
          >
            <button
              type="button"
              className="cfo-statement-advanced-resize"
              aria-label="Resize advanced controls"
              aria-orientation="vertical"
              aria-valuemax={STATEMENT_ADVANCED_PANEL_MAX_WIDTH}
              aria-valuemin={STATEMENT_ADVANCED_PANEL_MIN_WIDTH}
              aria-valuenow={advancedPanelWidth}
              role="separator"
              onMouseDown={startAdvancedPanelResize}
              onKeyDown={(event) => {
                if (event.key === 'ArrowLeft') {
                  event.preventDefault()
                  resizeAdvancedPanelBy(24)
                }
                if (event.key === 'ArrowRight') {
                  event.preventDefault()
                  resizeAdvancedPanelBy(-24)
                }
              }}
            >
              <GripVertical size={15} aria-hidden="true" />
            </button>
            <div className="cfo-statement-advanced-head">
              <div>
                <h3>Advanced</h3>
                <p>{selectedRows.length ? `${selectedRows.length} row${selectedRows.length === 1 ? '' : 's'} selected` : selectedCellSummary ? `${selectedCellSummary.count} cell${selectedCellSummary.count === 1 ? '' : 's'} selected` : 'Select rows in the grid to edit this view.'}</p>
              </div>
              <button type="button" className="btn icon" aria-label="Close advanced controls" onClick={() => setAdvancedOpen(false)}>
                <X size={16} aria-hidden="true" />
              </button>
            </div>
            {layoutName ? (
              <div className="cfo-statement-advanced-section cfo-statement-layout-summary">
                <h4>Layout</h4>
                <p>{layoutName}{layoutEdit?.layoutStatus ? ` · ${layoutEdit.layoutStatus}` : ''}</p>
              </div>
            ) : null}
            {toolbarActions ? (
              <div className="cfo-statement-advanced-section">
                <h4>Export</h4>
                <div className="cfo-statement-table-actions">{toolbarActions}</div>
              </div>
            ) : null}
            <div className="cfo-statement-advanced-section cfo-statement-applied-changes-section">
              <h4>Applied Changes</h4>
              <div className="cfo-statement-review-actions">
                <button type="button" className="btn" disabled={!undoStack.length || !layoutEditable} onClick={() => void undoLayoutEdit()}><Undo2 size={14} aria-hidden="true" /> Undo</button>
                <button type="button" className="btn" disabled={!redoStack.length || !layoutEditable} onClick={() => void redoLayoutEdit()}><Redo2 size={14} aria-hidden="true" /> Redo</button>
              </div>
              <div className="cfo-statement-change-drawer" aria-label="Applied layout changes">
                <div className="cfo-statement-change-drawer-head">
                  <strong>Layout changes</strong>
                  <span>{layoutChangeCards.length} applied</span>
                </div>
                {layoutChangeCards.length ? (
                  <div className="cfo-statement-change-list">
                    {layoutChangeCards.map((entry) => {
                      const canEdit = Boolean(entry.nodeId && (layoutNodesById.has(entry.nodeId) || !isEmptyLayoutChangePatch(entry.after)))
                      return (
                        <div key={entry.id} className="cfo-statement-change-row">
                          <strong>{layoutChangeRowLabel(entry, layoutNodesById)}</strong>
                          <small>{layoutChangeSummary(entry, layoutNodesById)}</small>
                          <div className="cfo-statement-change-row-actions">
                            <button type="button" className="btn" disabled={!canEdit} onClick={() => openLayoutChangeEditor(entry)}>Edit</button>
                            <button type="button" className="btn danger" disabled={!layoutEditable} onClick={() => void deleteLayoutChange(entry)}>Delete</button>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <p>No applied layout changes.</p>
                )}
              </div>
            </div>
            <div className="cfo-statement-advanced-section">
              <h4>Current View</h4>
              <button type="button" className="btn" disabled={selectedRows.length < 2 || Boolean(layoutEdit && !layoutEditable)} onClick={() => void createMergeGroup('merge')}>Merge rows</button>
              <button type="button" className="btn" disabled={selectedRows.length < 2 || Boolean(layoutEdit && !layoutEditable)} onClick={() => void createMergeGroup('rollup')}>Roll up selected</button>
              <button type="button" className="btn" disabled={!selectedLayoutNodes.some((node) => node.metadata?.cfoCoreMerge || node.metadata?.cfoCoreUserMergedGroup) || !layoutEditable} onClick={() => void unmergeSelectedRows()}>Unmerge</button>
              <button type="button" className="btn" disabled={!selectedRows.length || Boolean(layoutEdit && !layoutEditable)} onClick={() => void hideSelectedRows()}>Hide selected</button>
              <button type="button" className="btn" disabled={!selectedLayoutNodes.length || !layoutEditable} onClick={() => void showSelectedRows()}>Show selected</button>
              <label className="cfo-statement-advanced-check">
                <input type="checkbox" checked={settings.hideBlankRows} onChange={(event) => updateSetting('hideBlankRows', event.target.checked)} />
                <span>Hide blank rows</span>
              </label>
              <label className="cfo-statement-advanced-check">
                <input type="checkbox" checked={showHiddenLayoutRows} onChange={(event) => setShowHiddenLayoutRows(event.target.checked)} />
                <span>Show hidden rows{hiddenLayoutNodeCount ? ` (${hiddenLayoutNodeCount})` : ''}</span>
              </label>
              <button type="button" className="btn" disabled={!localHiddenRowIds.size && !mergeGroups.length && !hiddenLayoutNodeCount} onClick={() => void showAllLocalRows()}>Show all rows</button>
              <button type="button" className="btn" disabled={!selectedRows.length && !cellSelection} onClick={() => { rowSelectionAnchorIdRef.current = null; setSelectedRowIds(new Set()); clearCellSelection() }}>Clear selection</button>
            </div>
            <div className="cfo-statement-advanced-section">
              <h4>Layout Editing</h4>
              <button type="button" className="btn" disabled={!selectedLayoutNodes.length || !layoutEditable} onClick={() => selectedRows[0] && openRowEditor(selectedRows[0])}>Rename row</button>
              <button type="button" className="btn" disabled={!selectedLayoutNodes.length || !layoutEditable} onClick={() => void moveSelectedNode(-1)}>Move up</button>
              <button type="button" className="btn" disabled={!selectedLayoutNodes.length || !layoutEditable} onClick={() => void moveSelectedNode(1)}>Move down</button>
              <button type="button" className="btn" disabled={!selectedLayoutNodes.length || !layoutEditable} onClick={() => void indentSelectedNode()}>Indent</button>
              <button type="button" className="btn" disabled={!selectedLayoutNodes.length || !layoutEditable} onClick={() => void outdentSelectedNode()}>Outdent</button>
              <button type="button" className="btn" disabled={!selectedLayoutNodes.length || !layoutEditable} onClick={() => void insertRowNear()}>Insert row</button>
              <button type="button" className="btn" disabled={!journalEntryTargetNode || !layoutEdit?.onOpenJournalEntry} onClick={() => journalEntryTargetNode && layoutEdit?.onOpenJournalEntry?.(journalEntryTargetNode.id)}>Journal entry</button>
            </div>
            <div className="cfo-statement-advanced-section">
              <h4>Review</h4>
              <div className="cfo-statement-review-actions">
                <button type="button" className="btn" disabled={!undoStack.length || !layoutEditable} onClick={() => void undoLayoutEdit()}><Undo2 size={14} aria-hidden="true" /> Undo</button>
                <button type="button" className="btn" disabled={!redoStack.length || !layoutEditable} onClick={() => void redoLayoutEdit()}><Redo2 size={14} aria-hidden="true" /> Redo</button>
              </div>
              <button type="button" className="btn" disabled={!layoutEdit?.onValidate} onClick={() => void layoutEdit?.onValidate?.()}>Validate</button>
              <button type="button" className="btn" disabled={!layoutEdit?.onPublish || !layoutEditable} onClick={() => void layoutEdit?.onPublish?.()}>Publish</button>
            </div>
          </aside>
        ) : null}
      </div>
      {rowEditor ? (
        <div className="cfo-statement-settings-backdrop" role="presentation" onMouseDown={() => setRowEditor(null)}>
          <section
            className="cfo-statement-settings-modal cfo-statement-row-editor"
            role="dialog"
            aria-modal="true"
            aria-label="Edit statement row"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="cfo-statement-settings-head">
              <div>
                <h3>Edit row</h3>
                <p>Rename or reclassify this reporting row in the active layout.</p>
              </div>
              <button type="button" className="btn icon" aria-label="Close row editor" onClick={() => setRowEditor(null)}>
                <X size={16} aria-hidden="true" />
              </button>
            </div>
            <div className="cfo-statement-row-editor-grid">
              <label>
                <span>Name</span>
                <input value={rowEditor.displayName} onChange={(event) => setRowEditor((current) => current ? { ...current, displayName: event.target.value } : current)} />
              </label>
              <label>
                <span>Statement</span>
                <select value={rowEditor.statementType} onChange={(event) => setRowEditor((current) => current ? { ...current, statementType: event.target.value } : current)}>
                  <option value="profit_and_loss">Profit & Loss</option>
                  <option value="balance_sheet">Balance sheet</option>
                  <option value="cash_flow">Cash flow</option>
                  <option value="excluded">Excluded</option>
                </select>
              </label>
              <label className="cfo-statement-advanced-check">
                <input type="checkbox" checked={rowEditor.includeInReports} onChange={(event) => setRowEditor((current) => current ? { ...current, includeInReports: event.target.checked } : current)} />
                <span>Include in reports</span>
              </label>
            </div>
            <div className="cfo-statement-modal-actions">
              <button type="button" className="btn" onClick={() => setRowEditor(null)}>Cancel</button>
              <button type="button" className="btn primary" disabled={!rowEditor.displayName.trim() || !layoutEditable} onClick={() => void saveRowEditor()}>Save row</button>
            </div>
          </section>
        </div>
      ) : null}
      {contextMenu ? (
        <div
          className="cfo-statement-context-menu"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          role="menu"
          onMouseDown={(event) => event.stopPropagation()}
        >
          <button type="button" role="menuitem" disabled={!layoutNodesById.has(statementSourceNodeId(contextMenu.row.node_id)) || !layoutEditable} onClick={() => {
            openRowEditor(contextMenu.row)
            setContextMenu(null)
          }}>
            Rename row
          </button>
          <button type="button" role="menuitem" disabled={!layoutNodesById.has(statementSourceNodeId(contextMenu.row.node_id)) || !layoutEditable} onClick={() => {
            void moveLayoutNode(layoutNodesById.get(statementSourceNodeId(contextMenu.row.node_id)), -1)
            setContextMenu(null)
          }}>
            Move up
          </button>
          <button type="button" role="menuitem" disabled={!layoutNodesById.has(statementSourceNodeId(contextMenu.row.node_id)) || !layoutEditable} onClick={() => {
            void moveLayoutNode(layoutNodesById.get(statementSourceNodeId(contextMenu.row.node_id)), 1)
            setContextMenu(null)
          }}>
            Move down
          </button>
          <button type="button" role="menuitem" disabled={!layoutNodesById.has(statementSourceNodeId(contextMenu.row.node_id)) || !layoutEditable} onClick={() => {
            void insertRowNear(contextMenu.row)
            setContextMenu(null)
          }}>
            Insert row
          </button>
          <button type="button" role="menuitem" disabled={!layoutNodesById.has(statementSourceNodeId(contextMenu.row.node_id)) || !layoutEditable} onClick={() => {
            void indentLayoutNode(layoutNodesById.get(statementSourceNodeId(contextMenu.row.node_id)))
            setContextMenu(null)
          }}>
            Indent
          </button>
          <button type="button" role="menuitem" disabled={!layoutNodesById.has(statementSourceNodeId(contextMenu.row.node_id)) || !layoutEditable} onClick={() => {
            void outdentLayoutNode(layoutNodesById.get(statementSourceNodeId(contextMenu.row.node_id)))
            setContextMenu(null)
          }}>
            Outdent
          </button>
          <button type="button" role="menuitem" disabled={selectedRows.length < 2 || !layoutEditable} onClick={() => {
            void createMergeGroup('merge')
            setContextMenu(null)
          }}>
            Merge selected rows
          </button>
          <button type="button" role="menuitem" disabled={!selectedLayoutNodes.some((node) => node.metadata?.cfoCoreMerge || node.metadata?.cfoCoreUserMergedGroup) || !layoutEditable} onClick={() => {
            void unmergeSelectedRows()
            setContextMenu(null)
          }}>
            Unmerge selected rows
          </button>
          <button type="button" role="menuitem" onClick={openGlDetailFromContext}>
            Open GL detail
          </button>
          <button type="button" role="menuitem" disabled={!layoutEdit?.onOpenJournalEntry || !layoutNodesById.has(statementSourceNodeId(contextMenu.row.node_id))} onClick={() => {
            const nodeId = statementSourceNodeId(contextMenu.row.node_id)
            layoutEdit?.onOpenJournalEntry?.(nodeId)
            setContextMenu(null)
          }}>
            Journal entry
          </button>
          <button type="button" role="menuitem" onClick={() => {
            rowSelectionAnchorIdRef.current = contextMenu.row.node_id
            setSelectedRowIds(new Set([contextMenu.row.node_id]))
            setContextMenu(null)
          }}>
            Select row
          </button>
          <button type="button" role="menuitem" onClick={() => setContextMenu(null)}>
            Close
          </button>
        </div>
      ) : null}
    </div>
  )
}
